import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useAccount, useWalletClient, usePublicClient, useBalance, useSwitchChain } from 'wagmi';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { parseUnits, formatUnits, type Address, createPublicClient, http, erc20Abi } from 'viem';
import { supabase, TABLES } from '../../utils/supabase';
import { getRelayCurrencies, RelayCurrency } from '../../utils/relayProtocol';
import { SwapSuccessToast, SwapToastData } from '../shared/Toast';
import { Connection, PublicKey, LAMPORTS_PER_SOL, TransactionInstruction, AddressLookupTableAccount } from '@solana/web3.js';
import { useZNSName } from '../../hooks/useZNSName';

// ============ Contract Address Helpers ============
// Check if string is an EVM address (0x + 40 hex chars)
const isEvmAddress = (str: string): boolean => {
  return /^0x[a-fA-F0-9]{40}$/.test(str);
};

// Check if string is a Solana address (32-44 base58 chars)
const isSolanaAddress = (str: string): boolean => {
  return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(str);
};

// Check if string is any valid contract address
const isContractAddress = (str: string): boolean => {
  return isEvmAddress(str) || isSolanaAddress(str);
};

// Supabase table mapping by chain ID
const SUPABASE_TABLES_BY_CHAIN: Record<number, string> = {
  57073: 'tokens', // Ink
  1: 'tokens_usd1', // Ethereum (USD1 launchpad tokens)
  143: 'tokens_monad', // Monad
};

// Fetch token logo from Supabase by contract address and chain
const fetchTokenLogoFromSupabase = async (
  address: string,
  chainId: number
): Promise<string | null> => {
  const tableName = SUPABASE_TABLES_BY_CHAIN[chainId];
  if (!tableName) return null;

  try {
    const normalizedAddress = address.toLowerCase();
    const { data, error } = await supabase
      .from(tableName)
      .select('logo_url, contract_address');

    if (error || !data) return null;
    
    const matchingToken = data.find(
      token => token.contract_address?.toLowerCase() === normalizedAddress
    );
    
    return matchingToken?.logo_url || null;
  } catch {
    return null;
  }
};

// Fetch full token info from Supabase by contract address and chain
const fetchTokenFromSupabase = async (
  address: string,
  chainId: number
): Promise<{
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logoUrl?: string;
  chainId: number;
} | null> => {
  const tableName = SUPABASE_TABLES_BY_CHAIN[chainId];
  if (!tableName) {
    console.log('[CustomSwap] No Supabase table for chain:', chainId);
    return null;
  }

  try {
    // Normalize address to lowercase for comparison
    const normalizedAddress = address.toLowerCase();
    console.log('[CustomSwap] Looking up token in', tableName, 'for address:', normalizedAddress);
    
    // Different tables have different schemas - tokens table doesn't have decimals
    const selectColumns = tableName === 'tokens' 
      ? 'contract_address, name, symbol, logo_url'
      : 'contract_address, name, symbol, logo_url, decimals';
    
    // Fetch all tokens and do case-insensitive match client-side (more reliable than ilike)
    const { data, error } = await supabase
      .from(tableName)
      .select(selectColumns);

    if (error) {
      console.log('[CustomSwap] Supabase error:', error);
      return null;
    }

    // Find matching token (case-insensitive)
    const tokenData = data as any[] || [];
    const matchingToken = tokenData.find(
      (token: any) => token.contract_address?.toLowerCase() === normalizedAddress
    );

    if (!matchingToken) {
      console.log('[CustomSwap] Token not found in Supabase table:', tableName);
      return null;
    }
    
    console.log('[CustomSwap] Found token in Supabase:', matchingToken.symbol, matchingToken.name);
    
    return {
      address: matchingToken.contract_address || address,
      symbol: matchingToken.symbol || 'UNKNOWN',
      name: matchingToken.name || 'Unknown Token',
      decimals: matchingToken.decimals || 18,
      logoUrl: matchingToken.logo_url || undefined,
      chainId: chainId,
    };
  } catch (err) {
    console.error('[CustomSwap] Supabase lookup error:', err);
    return null;
  }
};

// Fetch token info from Relay API by contract address
const fetchTokenFromRelay = async (
  address: string,
  chainId: number
): Promise<{
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logoUrl?: string;
  chainId: number;
} | null> => {
  console.log('[CustomSwap] Fetching token from Relay:', address, 'on chain:', chainId);
  
  try {
    // Use Relay's v2 currency endpoint with POST - this is what relay.link uses
    const response = await fetch(
      'https://api.relay.link/currencies/v2',
      {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chainIds: [chainId],
          address: address,
          limit: 1,
        }),
      }
    );

    console.log('[CustomSwap] Relay v2 response status:', response.status);

    if (response.ok) {
      const data = await response.json();
      console.log('[CustomSwap] Relay v2 response data:', data);
      
      if (data && Array.isArray(data) && data.length > 0) {
        const token = data[0];
        return {
          address: token.address || address,
          symbol: token.symbol || 'UNKNOWN',
          name: token.name || 'Unknown Token',
          decimals: token.decimals || 18,
          logoUrl: token.metadata?.logoURI || token.logoURI,
          chainId: chainId,
        };
      }
    }

    // Try searching by term (address) if direct address lookup didn't work
    console.log('[CustomSwap] Trying Relay search by term...');
    const searchResponse = await fetch(
      'https://api.relay.link/currencies/v2',
      {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chainIds: [chainId],
          term: address,
          limit: 5,
        }),
      }
    );
    
    if (searchResponse.ok) {
      const searchData = await searchResponse.json();
      console.log('[CustomSwap] Relay search response:', searchData);
      
      // Find exact match in results
      if (searchData && Array.isArray(searchData) && searchData.length > 0) {
        const exactMatch = searchData.find(
          (t: { address?: string }) => t.address?.toLowerCase() === address.toLowerCase()
        );
        if (exactMatch) {
          return {
            address: exactMatch.address || address,
            symbol: exactMatch.symbol || 'UNKNOWN',
            name: exactMatch.name || 'Unknown Token',
            decimals: exactMatch.decimals || 18,
            logoUrl: exactMatch.metadata?.logoURI || exactMatch.logoURI,
            chainId: chainId,
          };
        }
      }
    }

    // If Relay doesn't have the token, create a placeholder - Relay can still route it
    console.log('[CustomSwap] Token not found in Relay, creating placeholder for routing');
    return {
      address: address,
      symbol: `${address.slice(2, 6).toUpperCase()}`,
      name: 'Custom Token',
      decimals: 18,
      chainId: chainId,
    };
  } catch (error) {
    console.error('[CustomSwap] Error fetching token from Relay:', error);
    // Still return a placeholder so user can try to swap
    return {
      address: address,
      symbol: `${address.slice(2, 6).toUpperCase()}`,
      name: 'Custom Token',
      decimals: 18,
      chainId: chainId,
    };
  }
};

// Fetch Solana token info from Relay
const fetchSolanaTokenFromRelay = async (
  address: string
): Promise<{
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logoUrl?: string;
  chainId: number;
} | null> => {
  try {
    // Solana chain ID in Relay is 792703809
    const SOLANA_CHAIN = 792703809;
    const response = await fetch(
      'https://api.relay.link/currencies/v2',
      {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chainIds: [SOLANA_CHAIN],
          address: address,
          limit: 1,
        }),
      }
    );

    if (!response.ok) return null;

    const data = await response.json();
    
    if (data && Array.isArray(data) && data.length > 0) {
      const token = data[0];
      return {
        address: token.address || address,
        symbol: token.symbol || 'SPL',
        name: token.name || 'SPL Token',
        decimals: token.decimals || 9,
        logoUrl: token.metadata?.logoURI || token.logoURI,
        chainId: SOLANA_CHAIN,
      };
    }

    // Return placeholder for unknown Solana token
    return {
      address: address,
      symbol: `${address.slice(0, 4)}...${address.slice(-4)}`,
      name: 'SPL Token',
      decimals: 9,
      chainId: SOLANA_CHAIN,
    };
  } catch {
    return null;
  }
};

// ============ Types ============
interface TokenInfo {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logoUrl?: string;
  chainId: number;
  balance?: string;
  balanceUSD?: number;
}

interface SwapQuote {
  outputAmount: string;
  outputAmountUSD: number;
  priceImpact: number;
  route?: string;
  fees?: {
    gas: string;
    relay: string;
  };
  rawQuote?: any; // Full Relay quote response for execution
}

// ============ Styled Components ============
const WidgetContainer = styled.div`
  background: var(--color-bg-card, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 16px;
  padding: 20px;
  width: 100%;
  max-width: 380px;
`;

const WidgetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const WidgetTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const SettingsButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #ffffff;
  }
`;

const SwapSection = styled.div`
  background: var(--color-bg, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 4px;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 12px;
`;

const WalletIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: #10b981;
    border-radius: 50%;
  }
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const AmountInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  font-size: 28px;
  font-weight: 500;
  color: #ffffff;
  outline: none;
  min-width: 0;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const TokenSelector = styled.button<{ $hasToken: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.$hasToken ? 'rgba(255, 255, 255, 0.08)' : 'linear-gradient(135deg, #a855f7, #6366f1)'};
  border: 1px solid ${props => props.$hasToken ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border-radius: 20px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.$hasToken ? 'rgba(255, 255, 255, 0.12)' : 'linear-gradient(135deg, #9333ea, #4f46e5)'};
  }
`;

const TokenLogo = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
`;

const TokenLogoPlaceholder = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
`;

const TokenSymbol = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

const SelectTokenText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  white-space: nowrap;
`;

const ChevronIcon = styled.svg`
  width: 16px;
  height: 16px;
  color: rgba(255, 255, 255, 0.7);
`;

const USDValue = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BalanceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  flex-wrap: wrap;
  gap: 8px;
`;

const BalanceText = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  overflow: hidden;

  span {
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 120px;
  }
`;

const PercentageButtons = styled.div`
  display: flex;
  gap: 3px;
  flex-shrink: 0;
`;

const PercentButton = styled.button<{ $active?: boolean }>`
  padding: 3px 6px;
  font-size: 10px;
  font-weight: 600;
  background: ${props => props.$active ? 'rgba(168, 85, 247, 0.3)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.$active ? 'rgba(168, 85, 247, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 4px;
  color: ${props => props.$active ? '#a855f7' : 'rgba(255, 255, 255, 0.6)'};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: rgba(168, 85, 247, 0.2);
    border-color: rgba(168, 85, 247, 0.4);
    color: #a855f7;
  }
`;

// Wallet Selector for cross-chain swaps
const WalletSelectorContainer = styled.div`
  position: relative;
  margin-top: 8px;
`;

const WalletSelectorButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(168, 85, 247, 0.3);
  }

  span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    flex-shrink: 0;
  }
`;

const WalletSelectorDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: var(--color-bg-card, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 8px;
  overflow: hidden;
  z-index: 100;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  max-width: 100%;
  box-sizing: border-box;
`;

const WalletOption = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  background: ${props => props.$selected ? 'rgba(168, 85, 247, 0.1)' : 'transparent'};
  border: none;
  color: #ffffff;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  &:hover {
    background: rgba(168, 85, 247, 0.15);
  }
`;

const WalletOptionLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const WalletOptionType = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WalletOptionAddress = styled.span`
  font-family: monospace;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WalletBadge = styled.span<{ $type: 'privy' | 'external' }>`
  padding: 2px 6px;
  font-size: 8px;
  font-weight: 600;
  border-radius: 4px;
  background: ${props => props.$type === 'privy' 
    ? 'rgba(168, 85, 247, 0.2)' 
    : 'rgba(16, 185, 129, 0.2)'};
  color: ${props => props.$type === 'privy' ? '#a855f7' : '#10b981'};
  white-space: nowrap;
  flex-shrink: 0;
`;

const SwapDirectionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--color-bg-card, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 10px;
  cursor: pointer;
  margin: -8px auto;
  position: relative;
  z-index: 1;
  transition: all 0.2s;

  &:hover {
    background: var(--color-bg-hover, #000000);
    border-color: var(--color-border-light, #444444);
  }

  svg {
    width: 18px;
    height: 18px;
    color: rgba(255, 255, 255, 0.7);
  }
`;

const SwapButton = styled.button<{ $disabled: boolean }>`
  width: 100%;
  padding: 16px;
  background: ${props => props.$disabled 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'linear-gradient(135deg, #a855f7, #6366f1)'};
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.$disabled ? 'rgba(255, 255, 255, 0.3)' : '#ffffff'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  margin-top: 12px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 20px rgba(168, 85, 247, 0.3);
  }
`;

const QuoteInfo = styled.div`
  background: var(--color-bg, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 8px;
  padding: 12px;
  margin-top: 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const QuoteRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PoweredBy = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 16px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
`;

// ============ Token Selector Modal ============
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--color-bg-elevated, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 16px;
  width: 90%;
  max-width: 420px;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const ModalTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #ffffff;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  font-size: 14px;
  color: #ffffff;
  outline: none;
  margin: 16px 20px;
  box-sizing: border-box;
  width: calc(100% - 40px);

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    border-color: rgba(168, 85, 247, 0.5);
  }
`;

const TokenList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 8px 16px;
`;

const TokenItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  background: transparent;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const TokenItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TokenItemName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TokenItemSymbol = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const TokenItemBalance = styled.div`
  text-align: right;
`;

const TokenItemBalanceAmount = styled.div`
  font-size: 14px;
  color: #ffffff;
`;

const TokenItemBalanceUSD = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: rgba(255, 255, 255, 0.5);
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  svg {
    animation: spin 1s linear infinite;
  }
`;

// Filter dropdown styles
const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 20px 12px;
`;

const FilterDropdown = styled.div`
  position: relative;
  flex: 1;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: #ffffff;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.15);
  }
`;

const FilterButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterChainLogo = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 50%;
`;

const FilterDropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--color-bg-elevated, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 10px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 10;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const FilterOption = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: ${props => props.$isSelected ? 'rgba(168, 85, 247, 0.1)' : 'transparent'};
  border: none;
  color: #ffffff;
  font-size: 13px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &:first-child {
    border-radius: 10px 10px 0 0;
  }

  &:last-child {
    border-radius: 0 0 10px 10px;
  }
`;

const ChainLogo = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
`;

const TokenChainBadge = styled.img`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  position: absolute;
  bottom: -2px;
  right: -2px;
  border: 2px solid #0a0a0a;
`;

const TokenLogoWrapper = styled.div`
  position: relative;
  width: 36px;
  height: 36px;
`;

// ============ Constants ============
const RELAY_API_BASE = 'https://api.relay.link';
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
const SOL_NATIVE_ADDRESS = '11111111111111111111111111111111'; // Solana native SOL
const MAVRK_FEE_RECIPIENT = '0x6fef8c56f1e853b8e4bf9f57e02f04c34acbd5d1'; // Mavrk fee wallet (lowercase)
const APP_FEE_BPS = '100'; // 1% app fee (100 basis points)

// Supported chains for multi-chain swaps
// Note: Relay uses chainId 792703809 for Solana in their API
const SUPPORTED_CHAINS = [
  { id: 57073, name: 'Ink', logo: 'https://icons.llamao.fi/icons/chains/rsz_ink.jpg', nativeCurrency: 'ETH', vmType: 'evm' },
  { id: 1, name: 'Ethereum', logo: 'https://icons.llamao.fi/icons/chains/rsz_ethereum.jpg', nativeCurrency: 'ETH', vmType: 'evm' },
  { id: 8453, name: 'Base', logo: 'https://icons.llamao.fi/icons/chains/rsz_base.jpg', nativeCurrency: 'ETH', vmType: 'evm' },
  { id: 10, name: 'Optimism', logo: 'https://icons.llamao.fi/icons/chains/rsz_optimism.jpg', nativeCurrency: 'ETH', vmType: 'evm' },
  { id: 42161, name: 'Arbitrum', logo: 'https://icons.llamao.fi/icons/chains/rsz_arbitrum.jpg', nativeCurrency: 'ETH', vmType: 'evm' },
  { id: 137, name: 'Polygon', logo: 'https://icons.llamao.fi/icons/chains/rsz_polygon.jpg', nativeCurrency: 'POL', vmType: 'evm' },
  { id: 56, name: 'BSC', logo: 'https://icons.llamao.fi/icons/chains/rsz_binance.jpg', nativeCurrency: 'BNB', vmType: 'evm' },
  { id: 43114, name: 'Avalanche', logo: 'https://icons.llamao.fi/icons/chains/rsz_avalanche.jpg', nativeCurrency: 'AVAX', vmType: 'evm' },
  { id: 792703809, name: 'Solana', logo: 'https://icons.llamao.fi/icons/chains/rsz_solana.jpg', nativeCurrency: 'SOL', vmType: 'svm' },
] as const;

const INK_CHAIN_ID = 57073;

// Solana chain ID in Relay
const SOLANA_CHAIN_ID = 792703809;

// ============ Component Props ============
interface CustomSwapWidgetProps {
  /** Pre-selected output token (for trade modal integration) */
  preselectedToToken?: {
    address: string;
    symbol: string;
    name: string;
    chainId: number;
    logo?: string;
    decimals?: number;
    balance?: string;
    priceUSD?: number;
  } | null;
  /** Whether the widget is embedded in a modal (adjusts styling) */
  embedded?: boolean;
}

// ============ Component ============
const CustomSwapWidget: React.FC<CustomSwapWidgetProps> = ({ 
  preselectedToToken = null,
  embedded = false 
}) => {
  const { address, isConnected, chainId: connectedChainId } = useAccount();
  const { login, authenticated, user } = usePrivy();
  const { wallets } = useWallets();
  const { data: walletClient } = useWalletClient();
  const { switchChainAsync } = useSwitchChain();
  const publicClient = usePublicClient();
  
  // Get Solana wallet address - prioritize browser extension wallet, then externally connected wallet, then Privy embedded
  const getSolanaAddress = useCallback(() => {
    // FIRST: Check for browser extension wallet (Phantom, Solflare, etc.) - synchronously
    const solana = (window as any).phantom?.solana || (window as any).solana || (window as any).solflare;
    if (solana && solana.isConnected && solana.publicKey) {
      return solana.publicKey.toString();
    }
    
    // SECOND: Check localStorage for externally connected Solana wallet (user's self-custody)
    const storedSolana = localStorage.getItem('connectedSolanaWallet');
    if (storedSolana) {
      return storedSolana;
    }
    
    // Also check mavrk_local_solana_wallet
    const localSolana = localStorage.getItem('mavrk_local_solana_wallet');
    if (localSolana) {
      return localSolana;
    }
    
    // THIRD: Check for external Solana wallet in Privy's wallet list
    // Note: ConnectedWallet uses 'type' property ('ethereum' | 'solana')
    const allWallets = wallets as any[];
    const externalSolanaWallet = allWallets.find(
      w => w.type === 'solana' && w.walletClientType !== 'privy'
    );
    if (externalSolanaWallet?.address) {
      return externalSolanaWallet.address;
    }
    
    // FOURTH: Check user's linked accounts for any Solana wallet
    // Note: LinkedAccountWithMetadata is a union type, need to cast and check
    const linkedAccounts = (user?.linkedAccounts || []) as any[];
    const linkedSolana = linkedAccounts.find(
      account => account.type === 'wallet' && 
        (account.chainType === 'solana' || account.chainId === 'solana:mainnet')
    );
    if (linkedSolana?.address) {
      return linkedSolana.address;
    }
    
    // LAST: Fall back to Privy embedded Solana wallet from useWallets()
    const privySolanaWallet = allWallets.find(
      w => w.type === 'solana' && w.walletClientType === 'privy'
    );
    if (privySolanaWallet?.address) {
      return privySolanaWallet.address;
    }
    
    return null;
  }, [wallets, user]);

  const solanaAddress = getSolanaAddress();
  
  // Get EVM wallet address - prioritize external connected wallet over Privy embedded
  const getEvmAddress = useCallback(() => {
    // FIRST: Check for external EVM wallet in Privy's wallet list
    // Note: ConnectedWallet uses 'type' property ('ethereum' | 'solana')
    const externalEvmWallet = wallets.find(
      w => w.type === 'ethereum' && w.walletClientType !== 'privy'
    );
    if (externalEvmWallet?.address) {
      return externalEvmWallet.address;
    }
    
    // SECOND: Check user's linked accounts for any EVM wallet
    // Note: LinkedAccountWithMetadata is a union type, need to cast and check
    const linkedAccounts = (user?.linkedAccounts || []) as any[];
    const linkedEvm = linkedAccounts.find(
      account => account.type === 'wallet' && account.chainType === 'ethereum'
    );
    if (linkedEvm?.address) {
      return linkedEvm.address;
    }
    
    // THIRD: Fall back to Privy embedded EVM wallet from useWallets()
    const privyEvmWallet = wallets.find(
      w => w.type === 'ethereum' && w.walletClientType === 'privy'
    );
    if (privyEvmWallet?.address) {
      return privyEvmWallet.address;
    }
    
    // LAST: Fall back to wagmi's connected address
    return address || null;
  }, [wallets, user, address]);

  // Token states
  const [fromToken, setFromToken] = useState<TokenInfo | null>(null);
  const [toToken, setToToken] = useState<TokenInfo | null>(null);
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  
  // Pre-populate toToken when preselectedToToken is provided (for trade modal integration)
  useEffect(() => {
    if (preselectedToToken && !toToken) {
      setToToken({
        address: preselectedToToken.address,
        symbol: preselectedToToken.symbol,
        name: preselectedToToken.name,
        chainId: preselectedToToken.chainId,
        logoUrl: preselectedToToken.logo, // Map logo -> logoUrl
        decimals: preselectedToToken.decimals || 18,
        balance: preselectedToToken.balance || '0',
      });
      console.log('[CustomSwap] Pre-populated toToken from preselectedToToken:', preselectedToToken.symbol, 'logo:', preselectedToToken.logo);
    }
  }, [preselectedToToken]);
  
  // Quote states
  const [quote, setQuote] = useState<SwapQuote | null>(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  
  // Token list states
  const [availableTokens, setAvailableTokens] = useState<TokenInfo[]>([]);
  const [isLoadingTokens, setIsLoadingTokens] = useState(true);
  
  // Modal state
  const [selectorOpen, setSelectorOpen] = useState<'from' | 'to' | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChainFilter, setSelectedChainFilter] = useState<number | 'all'>('all');
  const [chainDropdownOpen, setChainDropdownOpen] = useState(false);
  
  // Custom token lookup state
  const [isLookingUpToken, setIsLookingUpToken] = useState(false);
  const [customToken, setCustomToken] = useState<TokenInfo | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  
  // Swap state
  const [isSwapping, setIsSwapping] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [approvalComplete, setApprovalComplete] = useState(false);
  
  // Toast notification state
  const [toastData, setToastData] = useState<SwapToastData | null>(null);
  
  // Wallet selector state (for destination - cross-chain swaps to Solana)
  const [walletSelectorOpen, setWalletSelectorOpen] = useState(false);
  const [selectedDestinationWallet, setSelectedDestinationWallet] = useState<{
    address: string;
    type: 'privy' | 'external';
    label: string;
  } | null>(null);
  
  // EVM destination wallet selector state (for choosing which EVM wallet to receive TO)
  const [evmDestinationSelectorOpen, setEvmDestinationSelectorOpen] = useState(false);
  const [selectedDestinationEvmWallet, setSelectedDestinationEvmWallet] = useState<{
    address: string;
    type: 'privy' | 'external';
    label: string;
  } | null>(null);
  
  // Source wallet selector state (for choosing which wallet to swap FROM)
  const [sourceWalletSelectorOpen, setSourceWalletSelectorOpen] = useState(false);
  const [selectedSourceWallet, setSelectedSourceWallet] = useState<{
    address: string;
    type: 'privy' | 'external';
    chainType: 'evm' | 'solana';
    label: string;
  } | null>(null);

  // Get ZNS name for selected source wallet (only for EVM wallets on Ink)
  const sourceWalletForZNS = selectedSourceWallet?.chainType === 'evm' ? selectedSourceWallet.address : undefined;
  const { znsName: sourceWalletZNS } = useZNSName(sourceWalletForZNS);

  // ============ Get All Available Solana Wallets ============
  // State to track browser-detected Solana wallet
  const [browserSolanaWallet, setBrowserSolanaWallet] = useState<string | null>(null);

  // Detect browser Solana wallet (Phantom, Solflare, etc.) on mount and when fromToken changes
  useEffect(() => {
    const detectBrowserWallet = async () => {
      const solana = (window as any).phantom?.solana || (window as any).solana || (window as any).solflare;
      if (solana) {
        try {
          if (solana.isConnected && solana.publicKey) {
            const pubkeyStr = solana.publicKey.toString();
            console.log('[CustomSwap] Detected connected browser Solana wallet:', pubkeyStr);
            setBrowserSolanaWallet(pubkeyStr);
            return;
          }
          // Try silent connect
          const response = await solana.connect({ onlyIfTrusted: true });
          if (response?.publicKey) {
            const pubkeyStr = response.publicKey.toString();
            console.log('[CustomSwap] Silent-connected to browser Solana wallet:', pubkeyStr);
            setBrowserSolanaWallet(pubkeyStr);
            return;
          }
        } catch (e) {
          // Not connected or not trusted
        }
      }
      setBrowserSolanaWallet(null);
    };

    detectBrowserWallet();
  }, []);

  const getAvailableSolanaWallets = useCallback(() => {
    const walletsList: Array<{ address: string; type: 'privy' | 'external'; label: string }> = [];
    
    // FIRST: Check for browser extension wallet (Phantom, Solflare, etc.)
    if (browserSolanaWallet) {
      walletsList.push({
        address: browserSolanaWallet,
        type: 'external',
        label: 'Phantom/Solflare',
      });
    }
    
    // Check localStorage for externally connected Solana wallet
    const storedSolana = localStorage.getItem('connectedSolanaWallet');
    if (storedSolana && !walletsList.some(w => w.address === storedSolana)) {
      walletsList.push({
        address: storedSolana,
        type: 'external',
        label: 'Connected Wallet',
      });
    }
    
    // Also check mavrk_local_solana_wallet (used by WalletConnect)
    const localSolana = localStorage.getItem('mavrk_local_solana_wallet');
    if (localSolana && !walletsList.some(w => w.address === localSolana)) {
      walletsList.push({
        address: localSolana,
        type: 'external',
        label: 'Connected Wallet',
      });
    }
    
    // Check for external Solana wallets in Privy's wallet list
    const allWalletsSolana = wallets as any[];
    const externalSolanaWallets = allWalletsSolana.filter(
      w => w.type === 'solana' && w.walletClientType !== 'privy'
    );
    externalSolanaWallets.forEach(w => {
      // Avoid duplicates
      if (!walletsList.some(existing => existing.address === w.address)) {
        walletsList.push({
          address: w.address,
          type: 'external',
          label: w.walletClientType || 'External Wallet',
        });
      }
    });
    
    // Check for Privy embedded Solana wallet from useWallets()
    const privySolanaWallet = allWalletsSolana.find(
      w => w.type === 'solana' && w.walletClientType === 'privy'
    );
    if (privySolanaWallet?.address && !walletsList.some(w => w.address === privySolanaWallet.address)) {
      walletsList.push({
        address: privySolanaWallet.address,
        type: 'privy',
        label: 'Privy Wallet',
      });
    }
    
    // Also check user.linkedAccounts for Privy embedded Solana wallet (Privy v3)
    const linkedAccounts = (user?.linkedAccounts || []) as any[];
    const embeddedSolanaFromLinked = linkedAccounts.find(account => 
      account.type === 'wallet' && 
      account.walletClientType === 'privy' && 
      (account.chainType === 'solana' || account.chainId === 'solana:mainnet')
    );
    if (embeddedSolanaFromLinked?.address && !walletsList.some(w => w.address === embeddedSolanaFromLinked.address)) {
      walletsList.push({
        address: embeddedSolanaFromLinked.address,
        type: 'privy',
        label: 'Privy Wallet',
      });
    }
    
    return walletsList;
  }, [wallets, user, browserSolanaWallet]);

  const availableSolanaWallets = getAvailableSolanaWallets();

  // ============ Get All Available EVM Wallets ============
  const getAvailableEvmWallets = useCallback(() => {
    const walletsList: Array<{ address: string; type: 'privy' | 'external'; chainType: 'evm'; label: string }> = [];
    
    // FIRST: Check for external EVM wallet in Privy's wallet list (self-custody)
    const externalEvmWallet = wallets.find(
      w => w.type === 'ethereum' && w.walletClientType !== 'privy'
    );
    if (externalEvmWallet?.address) {
      walletsList.push({
        address: externalEvmWallet.address,
        type: 'external',
        chainType: 'evm',
        label: `${externalEvmWallet.walletClientType || 'External'} Wallet`,
      });
    }
    
    // SECOND: Add wagmi's connected address if not already in list
    if (address && !walletsList.some(w => w.address.toLowerCase() === address.toLowerCase())) {
      walletsList.push({
        address: address,
        type: 'external',
        chainType: 'evm',
        label: 'Connected Wallet',
      });
    }
    
    // THIRD: Check for Privy embedded EVM wallet in useWallets()
    const privyEvmWallet = wallets.find(
      w => w.type === 'ethereum' && w.walletClientType === 'privy'
    );
    if (privyEvmWallet?.address && !walletsList.some(w => w.address.toLowerCase() === privyEvmWallet.address.toLowerCase())) {
      walletsList.push({
        address: privyEvmWallet.address,
        type: 'privy',
        chainType: 'evm',
        label: 'Privy Smart Wallet',
      });
    }
    
    // FOURTH: Also check user.linkedAccounts for Privy embedded EVM wallet (Privy v3 compatibility)
    // IMPORTANT: Only add if the wallet is ALSO present in useWallets() (meaning it's ready for transactions)
    const linkedAccounts = (user?.linkedAccounts || []) as any[];
    const embeddedEvmFromLinked = linkedAccounts.find(account => 
      account.type === 'wallet' && 
      account.walletClientType === 'privy' && 
      (account.chainType === 'ethereum' || account.chainId?.startsWith('eip155'))
    );
    
    // Check if this wallet is also available in useWallets() (ready for signing)
    const isEmbeddedWalletReady = embeddedEvmFromLinked?.address && wallets.some(w => 
      w.address?.toLowerCase() === embeddedEvmFromLinked.address.toLowerCase()
    );
    
    if (embeddedEvmFromLinked?.address && !walletsList.some(w => w.address.toLowerCase() === embeddedEvmFromLinked.address.toLowerCase())) {
      walletsList.push({
        address: embeddedEvmFromLinked.address,
        type: 'privy',
        chainType: 'evm',
        label: isEmbeddedWalletReady ? 'Privy Smart Wallet' : 'Privy Smart Wallet (Loading...)',
      });
    }
    
    // Also check for smart_wallet type in linkedAccounts
    const smartWalletFromLinked = linkedAccounts.find(account => 
      account.type === 'smart_wallet' || 
      (account.type === 'wallet' && account.walletClientType === 'privy')
    );
    
    const isSmartWalletReady = smartWalletFromLinked?.address && wallets.some(w => 
      w.address?.toLowerCase() === smartWalletFromLinked.address.toLowerCase()
    );
    
    if (smartWalletFromLinked?.address && !walletsList.some(w => w.address.toLowerCase() === smartWalletFromLinked.address.toLowerCase())) {
      walletsList.push({
        address: smartWalletFromLinked.address,
        type: 'privy',
        chainType: 'evm',
        label: isSmartWalletReady ? 'Privy Smart Wallet' : 'Privy Smart Wallet (Loading...)',
      });
    }
    
    // FALLBACK: Look for any EVM wallet in linkedAccounts that's not already in the list
    // This catches Privy embedded wallets that might have unexpected structure
    const evmWalletAccounts = linkedAccounts.filter(account => 
      account.type === 'wallet' && 
      account.address && 
      // Must be EVM-like (ethereum chainType or eip155 chainId)
      (account.chainType === 'ethereum' || account.chainId?.startsWith('eip155') || !account.chainType)
    );
    
    for (const account of evmWalletAccounts) {
      // Check if this is NOT an external wallet (external wallets have specific walletClientTypes)
      const isExternal = ['metamask', 'coinbase_wallet', 'phantom', 'rainbow', 'walletconnect', 'injected'].includes(account.walletClientType?.toLowerCase());
      
      if (!isExternal && account.address && !walletsList.some(w => w.address.toLowerCase() === account.address.toLowerCase())) {
        // Check if wallet is ready in useWallets()
        const isReady = wallets.some(w => w.address?.toLowerCase() === account.address.toLowerCase());
        
        // This is likely a Privy embedded wallet
        walletsList.push({
          address: account.address,
          type: 'privy',
          chainType: 'evm',
          label: isReady ? 'Privy Smart Wallet' : 'Privy Smart Wallet (Loading...)',
        });
        break; // Only add one Privy wallet
      }
    }
    
    return walletsList;
  }, [wallets, address, user]);

  const availableEvmWallets = getAvailableEvmWallets();

  // Set default destination wallet when available wallets change or To token changes to Solana
  useEffect(() => {
    if (toToken?.chainId === SOLANA_CHAIN_ID && availableSolanaWallets.length > 0 && !selectedDestinationWallet) {
      setSelectedDestinationWallet(availableSolanaWallets[0]);
    }
  }, [toToken?.chainId, availableSolanaWallets, selectedDestinationWallet]);
  
  // Set default EVM destination wallet when available - prioritize external wallets
  useEffect(() => {
    if (toToken?.chainId !== SOLANA_CHAIN_ID && availableEvmWallets.length > 0 && !selectedDestinationEvmWallet) {
      // Prioritize external wallet as default
      const externalWallet = availableEvmWallets.find(w => w.type === 'external');
      setSelectedDestinationEvmWallet(externalWallet || availableEvmWallets[0]);
    }
  }, [toToken?.chainId, availableEvmWallets, selectedDestinationEvmWallet]);
  
  // Set default source wallet based on selected from token chain - prioritize external wallets
  useEffect(() => {
    if (!fromToken) return;
    
    const isSolanaOrigin = fromToken.chainId === SOLANA_CHAIN_ID;
    
    if (isSolanaOrigin) {
      // For Solana origin, default to external wallet if available
      if (availableSolanaWallets.length > 0) {
        const externalSolana = availableSolanaWallets.find(w => w.type === 'external');
        if (!selectedSourceWallet || selectedSourceWallet.chainType !== 'solana') {
          setSelectedSourceWallet(externalSolana 
            ? { ...externalSolana, chainType: 'solana' } 
            : { ...availableSolanaWallets[0], chainType: 'solana' }
          );
        }
      }
    } else {
      // For EVM origin, default to external wallet if available
      if (availableEvmWallets.length > 0) {
        const externalEvm = availableEvmWallets.find(w => w.type === 'external');
        if (!selectedSourceWallet || selectedSourceWallet.chainType !== 'evm') {
          setSelectedSourceWallet(externalEvm || availableEvmWallets[0]);
        }
      }
    }
  }, [fromToken?.chainId, availableEvmWallets, availableSolanaWallets]);

  // Get the current destination address (for Solana, use selected wallet; otherwise use EVM address)
  const getDestinationAddress = useCallback(() => {
    if (toToken?.chainId === SOLANA_CHAIN_ID) {
      return selectedDestinationWallet?.address || solanaAddress;
    }
    // For EVM destinations, use selected EVM destination wallet if set
    return selectedDestinationEvmWallet?.address || address;
  }, [toToken?.chainId, selectedDestinationWallet, selectedDestinationEvmWallet, solanaAddress, address]);

  // ============ Balance Fetching ============
  // Get the source address for balance fetching (use selected source wallet if EVM, otherwise wagmi address)
  const sourceEvmAddress = selectedSourceWallet?.chainType === 'evm' 
    ? selectedSourceWallet.address as Address 
    : address;
  
  // Fetch balance for the "from" token (EVM only)
  const { data: fromTokenBalance, refetch: refetchFromBalance } = useBalance({
    address: sourceEvmAddress as Address | undefined,
    token: fromToken?.address === ZERO_ADDRESS ? undefined : fromToken?.address as Address | undefined,
    chainId: fromToken?.chainId !== SOLANA_CHAIN_ID ? fromToken?.chainId : undefined,
  });

  // Fetch balance for the "to" token (EVM only)
  const { data: toTokenBalance } = useBalance({
    address: address as Address | undefined,
    token: toToken?.address === ZERO_ADDRESS ? undefined : toToken?.address as Address | undefined,
    chainId: toToken?.chainId !== SOLANA_CHAIN_ID ? toToken?.chainId : undefined,
  });

  // Privy EVM balance state (fallback when wagmi isn't connected)
  const [privyEvmBalance, setPrivyEvmBalance] = useState<{ value: bigint; decimals: number } | null>(null);

  // Fetch balance for Privy EVM wallet when wagmi isn't connected
  useEffect(() => {
    const fetchPrivyEvmBalance = async () => {
      // Only fetch if using Privy wallet, not connected via wagmi, and fromToken is EVM
      const isPrivyWallet = selectedSourceWallet?.type === 'privy' && selectedSourceWallet?.chainType === 'evm';
      const needsPrivyFetch = isPrivyWallet && !isConnected && fromToken && fromToken.chainId !== SOLANA_CHAIN_ID;
      
      if (!needsPrivyFetch || !selectedSourceWallet?.address) {
        setPrivyEvmBalance(null);
        return;
      }

      try {
        const chainInfo = SUPPORTED_CHAINS.find(c => c.id === fromToken.chainId);
        if (!chainInfo) {
          console.log('[CustomSwap] Chain not found for Privy balance fetch:', fromToken.chainId);
          return;
        }

        // Get RPC URL for the chain
        const rpcUrls: Record<number, string> = {
          57073: 'https://rpc-gel-sepolia.inkonchain.com', // Ink
          1: 'https://ethereum.publicnode.com', // Ethereum
          8453: 'https://base.publicnode.com', // Base
          10: 'https://optimism.publicnode.com', // Optimism
          42161: 'https://arbitrum-one.publicnode.com', // Arbitrum
          137: 'https://polygon-bor-rpc.publicnode.com', // Polygon
          56: 'https://bsc.publicnode.com', // BSC
          43114: 'https://avalanche-c-chain-rpc.publicnode.com', // Avalanche
          143: 'https://testnet-rpc.monad.xyz', // Monad
        };

        const rpcUrl = rpcUrls[fromToken.chainId];
        if (!rpcUrl) {
          console.log('[CustomSwap] No RPC URL for chain:', fromToken.chainId);
          return;
        }

        // Create a public client for the specific chain
        const client = createPublicClient({
          transport: http(rpcUrl),
        });

        const walletAddress = selectedSourceWallet.address as Address;
        let balance: bigint;
        let decimals: number;

        if (fromToken.address === ZERO_ADDRESS || fromToken.address === '0x0000000000000000000000000000000000000000') {
          // Native token balance
          balance = await client.getBalance({ address: walletAddress });
          decimals = 18;
        } else {
          // ERC20 token balance
          balance = await client.readContract({
            address: fromToken.address as Address,
            abi: erc20Abi,
            functionName: 'balanceOf',
            args: [walletAddress],
          });
          decimals = fromToken.decimals || 18;
        }

        console.log('[CustomSwap] Privy EVM balance fetched:', formatUnits(balance, decimals), fromToken.symbol);
        setPrivyEvmBalance({ value: balance, decimals });
      } catch (error) {
        console.error('[CustomSwap] Error fetching Privy EVM balance:', error);
        setPrivyEvmBalance(null);
      }
    };

    fetchPrivyEvmBalance();
  }, [selectedSourceWallet, isConnected, fromToken?.chainId, fromToken?.address, fromToken?.symbol, fromToken?.decimals]);

  // Solana balance state
  const [solanaBalance, setSolanaBalance] = useState<{ value: bigint; decimals: number } | null>(null);
  const [solanaToBalance, setSolanaToBalance] = useState<{ value: bigint; decimals: number } | null>(null);

  // Get the connected Solana wallet (Phantom/Solflare) for signing transactions
  const getConnectedSolanaWallet = useCallback(async (): Promise<string | null> => {
    // Try to get from connected browser wallet first (Phantom, Solflare, etc.)
    const solana = (window as any).phantom?.solana || (window as any).solana || (window as any).solflare;
    
    if (solana) {
      try {
        // Try to get already connected wallet
        if (solana.isConnected && solana.publicKey) {
          const pubkeyStr = solana.publicKey.toString();
          console.log('[CustomSwap] Found connected browser Solana wallet:', pubkeyStr);
          return pubkeyStr;
        }
        // If not connected, try to auto-connect (only if trusted)
        const response = await solana.connect({ onlyIfTrusted: true });
        if (response?.publicKey) {
          const pubkeyStr = response.publicKey.toString();
          console.log('[CustomSwap] Auto-connected to browser Solana wallet:', pubkeyStr);
          return pubkeyStr;
        }
      } catch (e) {
        console.log('[CustomSwap] Could not auto-connect to Solana wallet:', e);
      }
    }
    
    // Fall back to stored addresses or Privy embedded wallet
    const fallbackAddr = getSolanaAddress();
    console.log('[CustomSwap] Using fallback Solana address:', fallbackAddr);
    return fallbackAddr;
  }, [getSolanaAddress]);

  // Fetch Solana balance when fromToken is on Solana
  useEffect(() => {
    const fetchSolanaBalance = async () => {
      if (fromToken?.chainId !== SOLANA_CHAIN_ID) {
        setSolanaBalance(null);
        return;
      }

      // Use selected source wallet if available, otherwise get connected wallet
      const solanaAddr = selectedSourceWallet?.chainType === 'solana' 
        ? selectedSourceWallet.address 
        : await getConnectedSolanaWallet();
      
      if (!solanaAddr) {
        console.log('[CustomSwap] No Solana address available for balance fetch');
        setSolanaBalance(null);
        return;
      }

      console.log('[CustomSwap] Fetching Solana balance for address:', solanaAddr);

      // Try multiple RPC endpoints - QuickNode first (from env), then fallbacks
      const quicknodeUrl = import.meta.env.VITE_SOLANA_RPC_URL;
      const RPC_ENDPOINTS = quicknodeUrl
        ? [
            quicknodeUrl, // QuickNode first if configured
            'https://solana.publicnode.com',
            'https://api.mainnet-beta.solana.com',
            'https://rpc.ankr.com/solana',
          ]
        : [
            'https://solana.publicnode.com',
            'https://api.mainnet-beta.solana.com',
            'https://rpc.ankr.com/solana',
          ];

      for (const rpc of RPC_ENDPOINTS) {
        try {
          const connection = new Connection(rpc, 'confirmed');
          const pubkey = new PublicKey(solanaAddr);
          
          // For native SOL
          if (fromToken.address === SOL_NATIVE_ADDRESS || fromToken.symbol === 'SOL') {
            const balance = await connection.getBalance(pubkey);
            console.log('[CustomSwap] SOL balance from', rpc, ':', balance / LAMPORTS_PER_SOL, 'SOL');
            setSolanaBalance({
              value: BigInt(balance),
              decimals: 9, // SOL has 9 decimals
            });
            return; // Success, exit loop
          } else {
            // For SPL tokens, need to fetch token account
            console.log('[CustomSwap] SPL token balance fetch not yet implemented');
            setSolanaBalance(null);
            return;
          }
        } catch (error) {
          console.warn('[CustomSwap] RPC failed:', rpc, error);
          // Try next RPC
        }
      }

      console.error('[CustomSwap] All Solana RPC endpoints failed');
      setSolanaBalance(null);
    };

    fetchSolanaBalance();
  }, [fromToken?.chainId, fromToken?.address, fromToken?.symbol, getConnectedSolanaWallet, selectedSourceWallet]);

  // Fetch Solana balance for "to" token
  useEffect(() => {
    const fetchSolanaToBalance = async () => {
      if (toToken?.chainId !== SOLANA_CHAIN_ID) {
        setSolanaToBalance(null);
        return;
      }

      const solanaAddr = selectedDestinationWallet?.address || getSolanaAddress();
      if (!solanaAddr) {
        setSolanaToBalance(null);
        return;
      }

      try {
        const rpcUrl = import.meta.env.VITE_SOLANA_RPC_URL || 'https://solana.publicnode.com';
        const connection = new Connection(rpcUrl, 'confirmed');
        const pubkey = new PublicKey(solanaAddr);
        
        if (toToken.address === SOL_NATIVE_ADDRESS || toToken.symbol === 'SOL') {
          const balance = await connection.getBalance(pubkey);
          setSolanaToBalance({
            value: BigInt(balance),
            decimals: 9,
          });
        } else {
          setSolanaToBalance(null);
        }
      } catch (error) {
        console.error('[CustomSwap] Error fetching Solana to balance:', error);
        setSolanaToBalance(null);
      }
    };

    fetchSolanaToBalance();
  }, [toToken?.chainId, toToken?.address, toToken?.symbol, selectedDestinationWallet?.address]);

  // Get effective balance for from token (EVM or Solana)
  const effectiveFromBalance = useMemo(() => {
    if (fromToken?.chainId === SOLANA_CHAIN_ID) {
      return solanaBalance;
    }
    return fromTokenBalance ? { value: fromTokenBalance.value, decimals: fromTokenBalance.decimals } : null;
  }, [fromToken?.chainId, solanaBalance, fromTokenBalance]);

  // Update token balances when they change
  useEffect(() => {
    if (fromToken && effectiveFromBalance) {
      setFromToken({
        ...fromToken,
        balance: formatUnits(effectiveFromBalance.value, effectiveFromBalance.decimals),
      });
    }
  }, [effectiveFromBalance?.value]);

  useEffect(() => {
    if (toToken && toTokenBalance) {
      setToToken({
        ...toToken,
        balance: formatUnits(toTokenBalance.value, toTokenBalance.decimals),
      });
    }
  }, [toTokenBalance?.value]);

  // Update Solana "to" balance
  useEffect(() => {
    if (toToken && toToken.chainId === SOLANA_CHAIN_ID && solanaToBalance) {
      setToToken({
        ...toToken,
        balance: formatUnits(solanaToBalance.value, solanaToBalance.decimals),
      });
    }
  }, [solanaToBalance?.value]);

  // ============ Percentage Click Handler ============
  const handlePercentageClick = useCallback((percent: number) => {
    if (!fromToken || !effectiveFromBalance) return;
    
    const balance = effectiveFromBalance.value;
    const amount = (balance * BigInt(percent)) / BigInt(100);
    const formattedAmount = formatUnits(amount, effectiveFromBalance.decimals);
    
    // Remove trailing zeros for cleaner display
    const cleanAmount = parseFloat(formattedAmount).toString();
    setFromAmount(cleanAmount);
  }, [fromToken, effectiveFromBalance]);

  // ============ Fetch Tokens ============
  useEffect(() => {
    const fetchTokens = async () => {
      setIsLoadingTokens(true);
      const tokens: TokenInfo[] = [];

      try {
        // 1. Fetch from Relay API for ALL supported chains (including Solana)
        const chainIds = SUPPORTED_CHAINS.map(c => c.id);
        console.log('[CustomSwap] Fetching tokens for chains:', chainIds);
        const relayCurrencies = await getRelayCurrencies(chainIds);
        console.log(`[CustomSwap] Got ${relayCurrencies.length} currencies from Relay`);
        relayCurrencies.forEach(currency => {
          tokens.push({
            address: currency.address,
            symbol: currency.symbol,
            name: currency.name,
            decimals: currency.decimals,
            logoUrl: currency.metadata?.logoURI,
            chainId: currency.chainId,
          });
        });

        // 2. Fetch from Supabase (for Ink tokens with our logos)
        const { data: supabaseTokens, error } = await supabase
          .from(TABLES.TOKENS)
          .select('*')
          .eq('network', 'ink')
          .order('created_at', { ascending: false })
          .limit(100);

        if (!error && supabaseTokens) {
          supabaseTokens.forEach(token => {
            // Check if already exists from Relay
            const exists = tokens.some(t => 
              t.address.toLowerCase() === token.contract_address?.toLowerCase()
            );
            
            if (!exists && token.contract_address) {
              tokens.push({
                address: token.contract_address,
                symbol: token.symbol || 'UNKNOWN',
                name: token.name || 'Unknown Token',
                decimals: token.decimals || 18,
                logoUrl: token.logo_url,
                chainId: INK_CHAIN_ID,
              });
            } else if (token.logo_url) {
              // Update logo if Supabase has one and Relay doesn't
              const existing = tokens.find(t => 
                t.address.toLowerCase() === token.contract_address?.toLowerCase()
              );
              if (existing && !existing.logoUrl) {
                existing.logoUrl = token.logo_url;
              }
            }
          });
        }

        // 3. Also fetch from Velodrome tokens table
        const { data: velodromeTokens, error: veloError } = await supabase
          .from('tokens_velodrome')
          .select('*')
          .eq('network', 'ink')
          .limit(50);

        if (!veloError && velodromeTokens) {
          velodromeTokens.forEach(token => {
            const exists = tokens.some(t => 
              t.address.toLowerCase() === token.contract_address?.toLowerCase()
            );
            
            if (!exists && token.contract_address) {
              tokens.push({
                address: token.contract_address,
                symbol: token.symbol || 'UNKNOWN',
                name: token.name || 'Unknown Token',
                decimals: token.decimals || 18,
                logoUrl: token.logo_url,
                chainId: INK_CHAIN_ID,
              });
            }
          });
        }

        // 4. Fetch from tokens_eth table (Ethereum Mavrk tokens)
        const { data: ethTokens, error: ethError } = await supabase
          .from('tokens_eth')
          .select('contract_address, symbol, name, decimals, logo_url')
          .not('contract_address', 'is', null)
          .limit(100);

        if (!ethError && ethTokens) {
          ethTokens.forEach(token => {
            const exists = tokens.some(t => 
              t.chainId === 1 && t.address.toLowerCase() === token.contract_address?.toLowerCase()
            );
            
            if (!exists && token.contract_address) {
              tokens.push({
                address: token.contract_address,
                symbol: token.symbol || 'UNKNOWN',
                name: token.name || 'Unknown Token',
                decimals: token.decimals || 18,
                logoUrl: token.logo_url,
                chainId: 1, // Ethereum mainnet
              });
            } else if (token.logo_url) {
              // Update logo if Supabase has one
              const existing = tokens.find(t => 
                t.chainId === 1 && t.address.toLowerCase() === token.contract_address?.toLowerCase()
              );
              if (existing && !existing.logoUrl) {
                existing.logoUrl = token.logo_url;
              }
            }
          });
          console.log(`[CustomSwap] Added ${ethTokens.length} tokens from tokens_eth`);
        }

        // 5. Fetch from tokens_monad table (Monad Mavrk tokens)
        // Note: tokens_monad table doesn't have a decimals column
        const { data: monadTokens, error: monadError } = await supabase
          .from('tokens_monad')
          .select('contract_address, symbol, name, logo_url')
          .not('contract_address', 'is', null)
          .limit(100);

        if (!monadError && monadTokens) {
          monadTokens.forEach((token: any) => {
            const exists = tokens.some(t => 
              t.chainId === 143 && t.address.toLowerCase() === token.contract_address?.toLowerCase()
            );
            
            if (!exists && token.contract_address) {
              tokens.push({
                address: token.contract_address,
                symbol: token.symbol || 'UNKNOWN',
                name: token.name || 'Unknown Token',
                decimals: 18, // Default to 18 since tokens_monad has no decimals column
                logoUrl: token.logo_url,
                chainId: 143, // Monad
              });
            } else if (token.logo_url) {
              // Update logo if Supabase has one
              const existing = tokens.find(t => 
                t.chainId === 143 && t.address.toLowerCase() === token.contract_address?.toLowerCase()
              );
              if (existing && !existing.logoUrl) {
                existing.logoUrl = token.logo_url;
              }
            }
          });
          console.log(`[CustomSwap] Added ${monadTokens.length} tokens from tokens_monad`);
        }

        // Add native tokens for each chain if not already present
        const nativeTokens = [
          { chainId: 57073, symbol: 'ETH', name: 'Ethereum', logo: 'https://cdn.zerion.io/eth.png' },
          { chainId: 1, symbol: 'ETH', name: 'Ethereum', logo: 'https://cdn.zerion.io/eth.png' },
          { chainId: 8453, symbol: 'ETH', name: 'Ethereum', logo: 'https://cdn.zerion.io/eth.png' },
          { chainId: 10, symbol: 'ETH', name: 'Ethereum', logo: 'https://cdn.zerion.io/eth.png' },
          { chainId: 42161, symbol: 'ETH', name: 'Ethereum', logo: 'https://cdn.zerion.io/eth.png' },
          { chainId: 137, symbol: 'POL', name: 'Polygon', logo: 'https://icons.llamao.fi/icons/chains/rsz_polygon.jpg' },
          { chainId: 56, symbol: 'BNB', name: 'BNB', logo: 'https://icons.llamao.fi/icons/chains/rsz_binance.jpg' },
          { chainId: 43114, symbol: 'AVAX', name: 'Avalanche', logo: 'https://icons.llamao.fi/icons/chains/rsz_avalanche.jpg' },
          { chainId: 143, symbol: 'MON', name: 'Monad', logo: 'https://icons.llamao.fi/icons/chains/rsz_monad.jpg' },
          { chainId: 792703809, symbol: 'SOL', name: 'Solana', logo: 'https://icons.llamao.fi/icons/chains/rsz_solana.jpg' },
        ];

        // Add native tokens for each chain - always add them to ensure they're available
        nativeTokens.forEach(native => {
          const hasNative = tokens.some(t => 
            t.chainId === native.chainId && 
            (t.address === ZERO_ADDRESS || t.symbol.toUpperCase() === native.symbol)
          );
          if (!hasNative) {
            console.log(`[CustomSwap] Adding native ${native.symbol} for chain ${native.chainId}`);
            tokens.unshift({
              address: ZERO_ADDRESS,
              symbol: native.symbol,
              name: native.name,
              decimals: native.symbol === 'SOL' ? 9 : 18,
              logoUrl: native.logo,
              chainId: native.chainId,
            });
          }
        });
        
        // Log native token count per chain for debugging
        const inkNatives = tokens.filter(t => t.chainId === 57073 && t.address === ZERO_ADDRESS);
        console.log(`[CustomSwap] Ink chain native tokens:`, inkNatives.map(t => t.symbol));

        // Deduplicate tokens by chainId + address (important: native tokens have same address on different chains)
        const uniqueTokens = tokens.filter((token, index, self) =>
          index === self.findIndex(t => 
            t.chainId === token.chainId && t.address.toLowerCase() === token.address.toLowerCase()
          )
        );

        console.log(`[CustomSwap] Total unique tokens: ${uniqueTokens.length}`);
        setAvailableTokens(uniqueTokens);
      } catch (error) {
        console.error('[CustomSwap] Error fetching tokens:', error);
      } finally {
        setIsLoadingTokens(false);
      }
    };

    fetchTokens();
  }, []);

  // ============ Fetch Quote ============
  const fetchQuote = useCallback(async () => {
    if (!fromToken || !toToken || !fromAmount || parseFloat(fromAmount) <= 0) {
      setQuote(null);
      setToAmount('');
      setNeedsApproval(false);
      setApprovalComplete(false);
      return;
    }

    setIsLoadingQuote(true);

    try {
      const amountWei = parseUnits(fromAmount, fromToken.decimals).toString();

      // Use the token's chain for cross-chain swaps
      const originChainId = fromToken.chainId;
      const destinationChainId = toToken.chainId;
      
      // Get the correct user address based on origin chain
      // Prioritize selected source wallet, then fall back to defaults
      const currentEvmAddress = selectedSourceWallet?.chainType === 'evm' 
        ? selectedSourceWallet.address 
        : getEvmAddress();
      const currentSolanaAddress = selectedSourceWallet?.chainType === 'solana' 
        ? selectedSourceWallet.address 
        : getSolanaAddress();
      
      // Determine user address (sender) based on origin chain
      const userAddress = originChainId === SOLANA_CHAIN_ID 
        ? currentSolanaAddress 
        : currentEvmAddress;
      
      // Determine recipient address based on destination chain (for cross-chain)
      let recipientAddress = destinationChainId === SOLANA_CHAIN_ID
        ? (selectedDestinationWallet?.address || currentSolanaAddress)
        : (selectedDestinationEvmWallet?.address || currentEvmAddress);
      
      if (!userAddress) {
        console.error('[CustomSwap] No user address available for origin chain');
        setQuote(null);
        setToAmount('');
        setIsLoadingQuote(false);
        return;
      }

      const payload: Record<string, any> = {
        user: userAddress,
        originChainId,
        destinationChainId,
        originCurrency: fromToken.address,
        destinationCurrency: toToken.address,
        amount: amountWei,
        tradeType: 'EXACT_INPUT',
        // Add slippage tolerance (3% = 300 bps)
        slippageTolerance: '300',
      };
      
      // Only add app fees for EVM-to-EVM swaps (Relay doesn't support app fees with Solana)
      const isEvmToEvm = originChainId !== SOLANA_CHAIN_ID && destinationChainId !== SOLANA_CHAIN_ID;
      if (isEvmToEvm) {
        payload.appFees = [
          {
            recipient: MAVRK_FEE_RECIPIENT,
            fee: APP_FEE_BPS
          }
        ];
      }
      
      // Add recipient if cross-chain (different destination wallet)
      if (originChainId !== destinationChainId || recipientAddress !== userAddress) {
        payload.recipient = recipientAddress;
      }
      
      // For Solana origins, request simpler routes to avoid tx size limits
      if (originChainId === SOLANA_CHAIN_ID) {
        payload.onlyConfirmedCapacity = true;
      }

      console.log('[CustomSwap] Fetching quote:', payload);

      const response = await fetch(`${RELAY_API_BASE}/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[CustomSwap] Quote error:', errorText);
        setQuote(null);
        setToAmount('');
        return;
      }

      const data = await response.json();
      console.log('[CustomSwap] Quote response:', data);

      // Parse quote response - store full response for execution
      if (data.steps && data.steps.length > 0) {
        const outputAmount = data.details?.currencyOut?.amountFormatted || '0';
        // Parse amountUsd as float since API returns it as string
        const outputAmountUSD = parseFloat(data.details?.currencyOut?.amountUsd) || 0;

        // Check if quote requires approval (has an approve step with incomplete status)
        const hasApprovalStep = data.steps.some((step: any) => 
          (step.id === 'approve' || step.kind === 'approve') &&
          step.items?.some((item: any) => item.status === 'incomplete')
        );
        
        console.log('[CustomSwap] Quote requires approval:', hasApprovalStep);
        setNeedsApproval(hasApprovalStep);
        setApprovalComplete(false); // Reset approval status for new quote

        setToAmount(outputAmount);
        setQuote({
          outputAmount,
          outputAmountUSD,
          priceImpact: data.details?.priceImpact || 0,
          fees: {
            gas: data.fees?.gas?.amountFormatted || '0',
            relay: data.fees?.relayer?.amountFormatted || '0',
          },
          // Store the full quote response for execution
          rawQuote: data,
        });
      } else {
        console.error('[CustomSwap] No steps in quote response:', data);
        setQuote(null);
        setToAmount('');
        setNeedsApproval(false);
        setApprovalComplete(false);
      }
    } catch (error) {
      console.error('[CustomSwap] Quote error:', error);
      setQuote(null);
      setToAmount('');
      setNeedsApproval(false);
      setApprovalComplete(false);
    } finally {
      setIsLoadingQuote(false);
    }
  }, [fromToken, toToken, fromAmount, getEvmAddress, getSolanaAddress, selectedDestinationWallet, selectedSourceWallet]);

  // Debounced quote fetch
  useEffect(() => {
    const timer = setTimeout(fetchQuote, 500);
    return () => clearTimeout(timer);
  }, [fetchQuote]);

  // ============ Custom Token Lookup by Contract Address ============
  useEffect(() => {
    const lookupCustomToken = async () => {
      const query = searchQuery.trim();
      console.log('[CustomSwap] Search query changed:', query, 'isContractAddress:', isContractAddress(query));
      
      // Reset lookup state
      setCustomToken(null);
      setLookupError(null);
      
      // Check if the search query is a valid contract address
      if (!query || !isContractAddress(query)) {
        console.log('[CustomSwap] Not a valid contract address, skipping lookup');
        return;
      }
      
      // Check if token already exists in availableTokens
      const existingToken = availableTokens.find(
        t => t.address.toLowerCase() === query.toLowerCase()
      );
      if (existingToken) {
        console.log('[CustomSwap] Token already in list:', existingToken.symbol);
        return;
      }
      
      setIsLookingUpToken(true);
      console.log('[CustomSwap] Looking up token by address:', query, 'on chain:', selectedChainFilter);
      
      try {
        let token: TokenInfo | null = null;
        
        // Determine which chain to look up based on address format
        if (isEvmAddress(query)) {
          // If a specific chain is selected, use that; otherwise try Ink first
          const chainToLookup = selectedChainFilter !== 'all' ? selectedChainFilter : 57073;
          
          // First, try to get full token info from our Supabase tables (better metadata)
          const supabaseToken = await fetchTokenFromSupabase(query, chainToLookup);
          
          if (supabaseToken) {
            console.log('[CustomSwap] Found token in Supabase:', supabaseToken);
            token = supabaseToken;
          } else {
            // If not in Supabase, try Relay
            const relayToken = await fetchTokenFromRelay(query, chainToLookup);
            
            if (relayToken) {
              token = relayToken;
              
              // Try to get logo from our Supabase tables (may have it even if full data isn't there)
              const supabaseLogo = await fetchTokenLogoFromSupabase(query, chainToLookup);
              if (supabaseLogo) {
                token.logoUrl = supabaseLogo;
              }
            }
          }
        } else if (isSolanaAddress(query)) {
          // Look up Solana token
          token = await fetchSolanaTokenFromRelay(query);
        }
        
        if (token) {
          console.log('[CustomSwap] Found custom token:', token);
          setCustomToken(token);
        } else {
          setLookupError('Token not found. Please verify the contract address.');
        }
      } catch (error) {
        console.error('[CustomSwap] Token lookup error:', error);
        setLookupError('Failed to lookup token. Please try again.');
      } finally {
        setIsLookingUpToken(false);
      }
    };
    
    // Debounce the lookup
    const timer = setTimeout(lookupCustomToken, 600);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedChainFilter, availableTokens]);

  // ============ Swap Direction ============
  const handleSwapDirection = () => {
    const tempToken = fromToken;
    const tempAmount = fromAmount;
    
    setFromToken(toToken);
    setToToken(tempToken);
    setFromAmount(toAmount);
    setToAmount(tempAmount);
  };

  // ============ Token Selection ============
  const handleTokenSelect = (token: TokenInfo) => {
    if (selectorOpen === 'from') {
      // Don't allow same token for both sides
      if (toToken?.address.toLowerCase() === token.address.toLowerCase()) {
        setToToken(null);
      }
      setFromToken(token);
    } else if (selectorOpen === 'to') {
      if (fromToken?.address.toLowerCase() === token.address.toLowerCase()) {
        setFromToken(null);
      }
      setToToken(token);
    }
    setSelectorOpen(null);
    setSearchQuery('');
    setCustomToken(null);
    setLookupError(null);
  };

  // Handle custom token selection (from lookup)
  const handleCustomTokenSelect = () => {
    if (!customToken) return;
    
    if (selectorOpen === 'from') {
      if (toToken?.address.toLowerCase() === customToken.address.toLowerCase()) {
        setToToken(null);
      }
      setFromToken(customToken);
    } else if (selectorOpen === 'to') {
      if (fromToken?.address.toLowerCase() === customToken.address.toLowerCase()) {
        setFromToken(null);
      }
      setToToken(customToken);
    }
    
    // Add to available tokens for future searches in this session
    setAvailableTokens(prev => {
      if (prev.some(t => t.address.toLowerCase() === customToken.address.toLowerCase())) {
        return prev;
      }
      return [...prev, customToken];
    });
    
    setSelectorOpen(null);
    setSearchQuery('');
    setCustomToken(null);
    setLookupError(null);
  };

  // ============ Priority Token Symbols (blue-chips that should appear first) ============
  const PRIORITY_SYMBOLS = [
    'ETH', 'WETH', 'SOL', 'WSOL', 'MON', 'WMON',  // Native currencies
    'USDC', 'USDT', 'DAI', 'USDC.e', // Stablecoins
    'WBTC', 'BTC', 'cbBTC',          // Bitcoin
    'OP', 'ARB', 'MATIC', 'POL', 'BNB', 'AVAX', // L1/L2 tokens
    'LINK', 'UNI', 'AAVE', 'CRV',    // DeFi blue-chips
    'stETH', 'wstETH', 'rETH',       // Liquid staking
    'INK',                           // Ink native
  ];

  // ============ Filtered Tokens ============
  const filteredTokens = useMemo(() => {
    let filtered = availableTokens;
    
    // Filter by chain
    if (selectedChainFilter !== 'all') {
      filtered = filtered.filter(token => token.chainId === selectedChainFilter);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(token =>
        token.symbol.toLowerCase().includes(query) ||
        token.name.toLowerCase().includes(query) ||
        token.address.toLowerCase().includes(query)
      );
    }
    
    // Sort tokens: Native currencies first, then blue-chips, then others alphabetically
    filtered.sort((a, b) => {
      const aSymbol = a.symbol.toUpperCase();
      const bSymbol = b.symbol.toUpperCase();
      
      // Native tokens (zero address) always first
      const isANative = a.address === ZERO_ADDRESS || 
        ['ETH', 'SOL', 'MON', 'BNB', 'AVAX', 'POL', 'MATIC'].includes(aSymbol);
      const isBNative = b.address === ZERO_ADDRESS || 
        ['ETH', 'SOL', 'MON', 'BNB', 'AVAX', 'POL', 'MATIC'].includes(bSymbol);
      
      if (isANative && !isBNative) return -1;
      if (!isANative && isBNative) return 1;
      
      // Then priority tokens (blue-chips)
      const aIndex = PRIORITY_SYMBOLS.findIndex(s => aSymbol === s || aSymbol.startsWith(s));
      const bIndex = PRIORITY_SYMBOLS.findIndex(s => bSymbol === s || bSymbol.startsWith(s));
      
      const aIsPriority = aIndex !== -1;
      const bIsPriority = bIndex !== -1;
      
      if (aIsPriority && !bIsPriority) return -1;
      if (!aIsPriority && bIsPriority) return 1;
      if (aIsPriority && bIsPriority) return aIndex - bIndex;
      
      // Finally sort alphabetically by symbol
      return aSymbol.localeCompare(bSymbol);
    });
    
    return filtered;
  }, [availableTokens, searchQuery, selectedChainFilter]);

  // Get selected chain info
  const getChainInfo = (chainId: number) => {
    return SUPPORTED_CHAINS.find(c => c.id === chainId);
  };

  // ============ Execute Solana Transaction ============
  const executeSolanaSwap = async (quoteData: any): Promise<string | undefined> => {
    console.log('[CustomSwap] Executing Solana swap');
    
    // Get the Solana wallet
    const solana = (window as any).phantom?.solana || (window as any).solana || (window as any).solflare;
    
    if (!solana) {
      throw new Error('No Solana wallet found. Please install Phantom or Solflare to swap from Solana.');
    }
    
    // Ensure wallet is connected
    if (!solana.isConnected) {
      try {
        await solana.connect();
      } catch (connectError) {
        throw new Error('Please connect your Solana wallet (Phantom/Solflare) to complete this swap');
      }
    }
    
    const solanaAddress = solana.publicKey?.toString();
    if (!solanaAddress) {
      throw new Error('Could not get Solana wallet address');
    }
    
    console.log('[CustomSwap] Solana wallet address:', solanaAddress);
    
    // Import Solana web3.js
    const { 
      Connection, 
      PublicKey, 
      TransactionInstruction, 
      TransactionMessage, 
      VersionedTransaction,
      AddressLookupTableAccount
    } = await import('@solana/web3.js');
    
    // Extract instructions from the quote data
    let instructionsData: any[] = [];
    let addressLookupTableAddresses: string[] = [];
    let serializedTx: string | null = null;
    
    // Search for instructions or serialized transaction in the quote steps
    if (quoteData.steps) {
      for (const step of quoteData.steps) {
        if (step.items) {
          for (const item of step.items) {
            // Check for raw instructions (Relay's Solana format)
            if (item.data?.instructions && Array.isArray(item.data.instructions)) {
              instructionsData = item.data.instructions;
              addressLookupTableAddresses = item.data.addressLookupTableAddresses || [];
              console.log('[CustomSwap] Found', instructionsData.length, 'Solana instructions');
              console.log('[CustomSwap] Address lookup tables:', addressLookupTableAddresses.length);
              break;
            }
            // Also check for pre-serialized transaction
            if (item.data?.transaction || item.data?.serializedTransaction) {
              serializedTx = item.data.serializedTransaction || item.data.transaction;
              console.log('[CustomSwap] Found serialized transaction');
              break;
            }
          }
        }
        if (instructionsData.length > 0 || serializedTx) break;
      }
    }
    
    const rpcUrl = import.meta.env.VITE_SOLANA_RPC_URL || 'https://solana.publicnode.com';
    const connection = new Connection(rpcUrl, 'confirmed');
    
    // If we have a pre-serialized transaction, use it directly
    if (serializedTx) {
      console.log('[CustomSwap] Using pre-serialized transaction');
      try {
        const txBuffer = Buffer.from(serializedTx, 'base64');
        const versionedTx = VersionedTransaction.deserialize(txBuffer);
        const signedTx = await solana.signTransaction(versionedTx);
        
        const signature = await connection.sendRawTransaction(signedTx.serialize(), {
          skipPreflight: false,
          maxRetries: 3,
        });
        
        await connection.confirmTransaction(signature, 'confirmed');
        console.log('[CustomSwap] Solana transaction confirmed:', signature);
        return signature;
      } catch (txError: any) {
        console.error('[CustomSwap] Failed to send serialized tx:', txError);
        throw new Error(`Failed to execute Solana transaction: ${txError.message}`);
      }
    }
    
    // Build transaction from instructions
    if (instructionsData.length === 0) {
      throw new Error('No Solana transaction data found in quote. This swap route may not be available.');
    }
    
    console.log('[CustomSwap] Building Solana transaction from', instructionsData.length, 'instructions...');
    
    // Convert instruction data to TransactionInstruction objects
    const instructions: TransactionInstruction[] = instructionsData.map((ix: any) => {
      // Handle hex-encoded data
      let dataBuffer: Buffer;
      if (typeof ix.data === 'string') {
        // Check if it's hex (no 0x prefix in Relay format)
        dataBuffer = Buffer.from(ix.data, 'hex');
      } else if (ix.data instanceof Uint8Array || Array.isArray(ix.data)) {
        dataBuffer = Buffer.from(ix.data);
      } else {
        dataBuffer = Buffer.alloc(0);
      }
      
      return new TransactionInstruction({
        programId: new PublicKey(ix.programId),
        keys: ix.keys.map((key: any) => ({
          pubkey: new PublicKey(key.pubkey),
          isSigner: key.isSigner,
          isWritable: key.isWritable,
        })),
        data: dataBuffer,
      });
    });
    
    console.log('[CustomSwap] Built', instructions.length, 'instructions');
    
    // Fetch address lookup tables if any
    let addressLookupTableAccounts: AddressLookupTableAccount[] = [];
    if (addressLookupTableAddresses.length > 0) {
      console.log('[CustomSwap] Fetching', addressLookupTableAddresses.length, 'address lookup tables...');
      
      for (const address of addressLookupTableAddresses) {
        try {
          const lookupTableAccount = await connection.getAddressLookupTable(new PublicKey(address));
          if (lookupTableAccount.value) {
            addressLookupTableAccounts.push(lookupTableAccount.value);
          }
        } catch (e) {
          console.warn('[CustomSwap] Failed to fetch lookup table:', address, e);
        }
      }
      console.log('[CustomSwap] Fetched', addressLookupTableAccounts.length, 'lookup tables');
    }
    
    // Get recent blockhash
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
    console.log('[CustomSwap] Got blockhash:', blockhash.substring(0, 20) + '...');
    
    // Create the transaction message
    const messageV0 = new TransactionMessage({
      payerKey: new PublicKey(solanaAddress),
      recentBlockhash: blockhash,
      instructions: instructions,
    }).compileToV0Message(addressLookupTableAccounts);
    
    // Create versioned transaction
    const transaction = new VersionedTransaction(messageV0);
    
    console.log('[CustomSwap] Requesting wallet signature...');
    
    // Sign the transaction with the wallet
    let signedTransaction;
    try {
      signedTransaction = await solana.signTransaction(transaction);
    } catch (signError: any) {
      if (signError.message?.includes('User rejected') || signError.message?.includes('rejected')) {
        throw new Error('Transaction cancelled by user');
      }
      throw new Error(`Failed to sign transaction: ${signError.message}`);
    }
    
    console.log('[CustomSwap] Transaction signed, sending...');
    
    // Send the transaction
    const signature = await connection.sendRawTransaction(signedTransaction.serialize(), {
      skipPreflight: false,
      maxRetries: 3,
    });
    
    console.log('[CustomSwap] Transaction sent:', signature);
    
    // Wait for confirmation
    const confirmation = await connection.confirmTransaction({
      signature,
      blockhash,
      lastValidBlockHeight,
    }, 'confirmed');
    
    if (confirmation.value.err) {
      throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
    }
    
    console.log('[CustomSwap] Solana transaction confirmed:', signature);
    return signature;
  };

  // ============ Fetch Fresh Quote for Execution ============
  const fetchFreshQuote = async (): Promise<any> => {
    if (!fromToken || !toToken || !fromAmount) return null;
    
    // Get the correct user address based on origin chain (same logic as fetchQuote)
    // Prioritize selected source wallet, then fall back to defaults
    const currentEvmAddress = selectedSourceWallet?.chainType === 'evm' 
      ? selectedSourceWallet.address 
      : getEvmAddress();
    const currentSolanaAddress = selectedSourceWallet?.chainType === 'solana' 
      ? selectedSourceWallet.address 
      : getSolanaAddress();
    
    // User address is the sender - use Solana address if origin is Solana
    const userAddress = fromToken.chainId === SOLANA_CHAIN_ID 
      ? currentSolanaAddress 
      : currentEvmAddress;
    
    // Recipient is on the destination chain
    const recipientAddress = toToken.chainId === SOLANA_CHAIN_ID 
      ? (selectedDestinationWallet?.address || currentSolanaAddress)
      : (selectedDestinationEvmWallet?.address || currentEvmAddress);
    
    if (!userAddress) {
      console.error('[CustomSwap] No user address available for fresh quote');
      return null;
    }

    const payload: any = {
      user: userAddress,
      originChainId: fromToken.chainId,
      destinationChainId: toToken.chainId,
      originCurrency: fromToken.address,
      destinationCurrency: toToken.address,
      amount: parseUnits(fromAmount, fromToken.decimals || 18).toString(),
      tradeType: 'EXACT_INPUT',
      referrer: 'mavrk.ink',
      slippageTolerance: "300",
    };

    // Add recipient for cross-chain swaps (when destination chain is different or recipient wallet is different)
    if (fromToken.chainId !== toToken.chainId || recipientAddress !== userAddress) {
      payload.recipient = recipientAddress;
    }

    // For Solana origin, add simpler routing
    if (fromToken.chainId === SOLANA_CHAIN_ID) {
      payload.onlyConfirmedCapacity = true;
    }

    console.log('[CustomSwap] Fetching fresh quote for swap execution:', payload);

    const response = await fetch(`${RELAY_API_BASE}/quote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to get fresh quote');
    }

    return await response.json();
  };

  // ============ Execute Swap ============
  const handleSwap = async () => {
    if (!fromToken || !toToken || !fromAmount || !quote?.rawQuote) {
      console.error('[CustomSwap] Missing required data for swap');
      return;
    }

    // Check if we have the right wallet for this swap
    const originChainId = fromToken.chainId;
    const isSolanaOrigin = originChainId === SOLANA_CHAIN_ID;
    
    // Check if the selected source wallet is a Privy wallet
    const isUsingPrivyWallet = selectedSourceWallet?.type === 'privy';
    
    // For EVM swaps, verify we have the correct wallet connected
    if (!isSolanaOrigin) {
      if (isUsingPrivyWallet) {
        // Check if the Privy wallet is actually ready (present in useWallets())
        const privyWalletReadyInUseWallets = wallets.some(w => 
          w.address?.toLowerCase() === selectedSourceWallet?.address.toLowerCase()
        );
        
        if (!privyWalletReadyInUseWallets) {
          // Privy wallet exists in linkedAccounts but not yet ready in useWallets()
          alert('Your Privy Smart Wallet is still loading. Please wait a moment and try again, or switch to your connected external wallet.');
          return;
        }
        
        // For Privy wallets, we need to get the provider from Privy
        // First try exact match, then try just by address
        console.log('[CustomSwap] Looking for Privy wallet:', selectedSourceWallet?.address);
        console.log('[CustomSwap] Available wallets from useWallets():', wallets.map(w => ({ 
          address: w.address, 
          walletClientType: w.walletClientType, 
          type: w.type 
        })));
        console.log('[CustomSwap] wagmi address:', address);
        console.log('[CustomSwap] user.linkedAccounts:', user?.linkedAccounts?.map((a: any) => ({
          type: a.type,
          walletClientType: a.walletClientType,
          chainType: a.chainType,
          address: a.address
        })));
        
        let privyWallet = wallets.find(w => 
          w.walletClientType === 'privy' && 
          w.type === 'ethereum' && 
          w.address.toLowerCase() === selectedSourceWallet?.address.toLowerCase()
        );
        
        // If not found, try matching just by address (Privy wallet might have different walletClientType)
        if (!privyWallet) {
          privyWallet = wallets.find(w => 
            w.type === 'ethereum' && 
            w.address.toLowerCase() === selectedSourceWallet?.address.toLowerCase()
          );
        }
        
        // FALLBACK: Try to find ANY wallet with matching address (regardless of chainType)
        if (!privyWallet) {
          privyWallet = wallets.find(w => 
            w.address?.toLowerCase() === selectedSourceWallet?.address.toLowerCase()
          );
          if (privyWallet) {
            console.log('[CustomSwap] Found wallet by address only:', privyWallet.walletClientType, privyWallet.type);
          }
        }
        
        if (!privyWallet) {
          console.error('[CustomSwap] Selected Privy wallet not found in wallets array');
          console.log('[CustomSwap] Checking if wagmi address matches selected wallet...');
          // Fall through to use walletClient if available
          if (!walletClient) {
            alert('Selected Privy wallet is not available. Please try refreshing the page.');
            return;
          }
        } else {
          console.log('[CustomSwap] Using Privy embedded wallet:', privyWallet.address);
        }
      } else if (!walletClient) {
        console.error('[CustomSwap] No wallet client available');
        return;
      }
      
      // Verify the connected wallet matches the selected source wallet
      if (selectedSourceWallet && address?.toLowerCase() !== selectedSourceWallet.address.toLowerCase() && !isUsingPrivyWallet) {
        console.warn('[CustomSwap] Selected source wallet does not match connected wallet');
        alert(`Please connect the wallet ${selectedSourceWallet.address.slice(0, 6)}...${selectedSourceWallet.address.slice(-4)} to execute this swap.`);
        return;
      }
    }

    // Check if we need to switch chains (for EVM tokens)
    if (!isSolanaOrigin && connectedChainId !== fromToken.chainId) {
      console.log(`[CustomSwap] Need to switch from chain ${connectedChainId} to ${fromToken.chainId} for swap`);
      try {
        await switchChainAsync({ chainId: fromToken.chainId });
        console.log('[CustomSwap] Chain switched successfully');
        // Small delay to let the wallet update
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (switchError: any) {
        console.error('[CustomSwap] Failed to switch chain:', switchError);
        if (switchError.message?.includes('User rejected')) {
          alert('Please switch to the correct network to complete this swap.');
        } else {
          const chainName = SUPPORTED_CHAINS.find(c => c.id === fromToken.chainId)?.name || 'correct';
          alert(`Please switch your wallet to the ${chainName} network.`);
        }
        return;
      }
    }

    setIsSwapping(true);

    try {
      // Always fetch a fresh quote before executing to avoid stale data
      console.log('[CustomSwap] Fetching fresh quote before swap execution...');
      const freshQuote = await fetchFreshQuote();
      
      if (!freshQuote || !freshQuote.steps || freshQuote.steps.length === 0) {
        throw new Error('Failed to get fresh quote - please try again');
      }
      
      // Check if fresh quote still requires approval (shouldn't happen if we just approved)
      const stillNeedsApproval = freshQuote.steps.some((step: any) => 
        (step.id === 'approve' || step.kind === 'approve') &&
        step.items?.some((item: any) => item.status === 'incomplete')
      );
      
      // Only reset to approval mode if we haven't already completed an approval
      // (Relay API can lag and still return approval steps even after approval is done)
      if (stillNeedsApproval && !approvalComplete) {
        console.log('[CustomSwap] Fresh quote requires approval - updating state');
        setQuote({
          ...quote,
          rawQuote: freshQuote,
        });
        setNeedsApproval(true);
        setIsSwapping(false);
        alert('Token approval required. Please approve first.');
        return;
      }
      
      // If we just completed an approval but quote still shows approval, skip it
      if (stillNeedsApproval && approvalComplete) {
        console.log('[CustomSwap] Approval already completed - skipping stale approval step from quote');
      }

      const quoteData = freshQuote;
      console.log('[CustomSwap] Executing swap with fresh quote:', quoteData);
      
      let lastTxHash: string | undefined;

      // Handle Solana origin swaps
      if (isSolanaOrigin) {
        try {
          lastTxHash = await executeSolanaSwap(quoteData);
          console.log('[CustomSwap] Solana swap completed:', lastTxHash);
        } catch (solanaError: any) {
          throw solanaError;
        }
      } else {
        // Execute EVM transaction steps from the quote
        // Skip approval steps (they are handled by handleApprove)
        if (quoteData.steps && quoteData.steps.length > 0) {
          for (const step of quoteData.steps) {
            const isApprovalStep = step.id === 'approve' || step.kind === 'approve';
            
            // Skip approval steps - they're handled separately
            if (isApprovalStep) {
              console.log('[CustomSwap] Skipping approval step (already done):', step.id);
              continue;
            }
            
            console.log('[CustomSwap] Processing swap step:', step.id, step.kind);
            
            if (step.items && step.items.length > 0) {
              for (const item of step.items) {
                // Check if item needs action
                if (item.status === 'incomplete' && item.data) {
                  const txData = item.data;
                  const gasLimit = txData.gas ? BigInt(txData.gas) : undefined;
                  
                  console.log('[CustomSwap] Sending swap transaction:', {
                    to: txData.to,
                    value: txData.value,
                    gasLimit: gasLimit?.toString(),
                  });

                  // Send the transaction - use Privy wallet if selected, otherwise wagmi
                  let txHash: `0x${string}`;
                  
                  if (isUsingPrivyWallet) {
                    // Get Privy wallet and its provider - try multiple matching strategies
                    let privyWallet = wallets.find(w => 
                      w.walletClientType === 'privy' && 
                      w.type === 'ethereum' && 
                      w.address.toLowerCase() === selectedSourceWallet?.address.toLowerCase()
                    );
                    
                    // Fallback: match by chainType ethereum and address
                    if (!privyWallet) {
                      privyWallet = wallets.find(w => 
                        w.type === 'ethereum' && 
                        w.address.toLowerCase() === selectedSourceWallet?.address.toLowerCase()
                      );
                    }
                    
                    // Fallback: match by walletClientType privy and address (chainType might be undefined)
                    if (!privyWallet) {
                      privyWallet = wallets.find(w => 
                        w.walletClientType === 'privy' && 
                        w.address.toLowerCase() === selectedSourceWallet?.address.toLowerCase()
                      );
                      if (privyWallet) {
                        console.log('[CustomSwap] Found Privy wallet by walletClientType + address');
                      }
                    }
                    
                    // Last fallback: match by address only
                    if (!privyWallet) {
                      privyWallet = wallets.find(w => 
                        w.address?.toLowerCase() === selectedSourceWallet?.address.toLowerCase()
                      );
                      if (privyWallet) {
                        console.log('[CustomSwap] Found wallet by address only:', privyWallet.walletClientType, privyWallet.type);
                      }
                    }
                    
                    if (!privyWallet) {
                      console.error('[CustomSwap] Privy wallet not found in wallets array');
                      // Check if wagmi walletClient is connected to the same address as the selected Privy wallet
                      if (walletClient && address?.toLowerCase() === selectedSourceWallet?.address.toLowerCase()) {
                        console.log('[CustomSwap] Using wagmi walletClient (same address as Privy wallet)');
                        txHash = await walletClient.sendTransaction({
                          to: txData.to as `0x${string}`,
                          data: txData.data as `0x${string}`,
                          value: BigInt(txData.value || 0),
                          gas: gasLimit,
                          maxFeePerGas: txData.maxFeePerGas ? BigInt(txData.maxFeePerGas) : undefined,
                          maxPriorityFeePerGas: txData.maxPriorityFeePerGas ? BigInt(txData.maxPriorityFeePerGas) : undefined,
                        });
                      } else {
                        // Cannot proceed - Privy wallet not available and wagmi is connected to different address
                        alert(`Privy Smart Wallet (${selectedSourceWallet?.address.slice(0, 6)}...${selectedSourceWallet?.address.slice(-4)}) is not ready. Please try again in a moment, or switch to your connected wallet.`);
                        setIsLoadingQuote(false);
                        return;
                      }
                    } else {
                      // Switch Privy wallet to correct chain if needed
                      await privyWallet.switchChain(fromToken.chainId);
                      
                      const provider = await privyWallet.getEthereumProvider();
                      const txRequest = {
                        to: txData.to,
                        data: txData.data,
                        value: txData.value ? `0x${BigInt(txData.value).toString(16)}` : '0x0',
                        gas: gasLimit ? `0x${gasLimit.toString(16)}` : undefined,
                        maxFeePerGas: txData.maxFeePerGas ? `0x${BigInt(txData.maxFeePerGas).toString(16)}` : undefined,
                        maxPriorityFeePerGas: txData.maxPriorityFeePerGas ? `0x${BigInt(txData.maxPriorityFeePerGas).toString(16)}` : undefined,
                      };
                      
                      console.log('[CustomSwap] Sending via Privy wallet:', txRequest);
                      txHash = await provider.request({
                        method: 'eth_sendTransaction',
                        params: [txRequest],
                      }) as `0x${string}`;
                    }
                  } else {
                    txHash = await walletClient!.sendTransaction({
                      to: txData.to as `0x${string}`,
                      data: txData.data as `0x${string}`,
                      value: BigInt(txData.value || 0),
                      gas: gasLimit,
                      maxFeePerGas: txData.maxFeePerGas ? BigInt(txData.maxFeePerGas) : undefined,
                      maxPriorityFeePerGas: txData.maxPriorityFeePerGas ? BigInt(txData.maxPriorityFeePerGas) : undefined,
                    });
                  }

                  console.log('[CustomSwap] Swap transaction sent:', txHash);
                  lastTxHash = txHash;

                  // Wait for confirmation - use publicClient if available, otherwise create one
                  const waitClient = publicClient || (() => {
                    const rpcUrls: Record<number, string> = {
                      57073: 'https://rpc-gel-sepolia.inkonchain.com',
                      1: 'https://ethereum.publicnode.com',
                      8453: 'https://base.publicnode.com',
                      10: 'https://optimism.publicnode.com',
                      42161: 'https://arbitrum-one.publicnode.com',
                      137: 'https://polygon-bor-rpc.publicnode.com',
                      56: 'https://bsc.publicnode.com',
                      43114: 'https://avalanche-c-chain-rpc.publicnode.com',
                      143: 'https://testnet-rpc.monad.xyz',
                    };
                    const rpcUrl = rpcUrls[fromToken.chainId];
                    return rpcUrl ? createPublicClient({ transport: http(rpcUrl) }) : null;
                  })();
                  
                  if (waitClient) {
                    try {
                      const receipt = await waitClient.waitForTransactionReceipt({ 
                        hash: txHash,
                        timeout: 120_000, // 2 minute timeout for cross-chain
                      });
                      
                      if (receipt.status === 'success') {
                        console.log('[CustomSwap] Swap transaction confirmed:', receipt);
                      } else {
                        throw new Error('Swap transaction reverted');
                      }
                    } catch (waitError) {
                      console.warn('[CustomSwap] Could not wait for receipt, tx may still be pending:', waitError);
                      // Don't throw - the tx was sent, just couldn't confirm
                    }
                  } else {
                    console.log('[CustomSwap] No client available to wait for receipt, tx hash:', txHash);
                  }
                }
              }
            }
          }
        } else {
          throw new Error('No transaction steps in quote');
        }
      }
        
      // Success - show toast notification
      const requestId = quoteData.requestId || quoteData.details?.requestId;
      setToastData({
        fromAmount: fromAmount,
        fromSymbol: fromToken.symbol,
        fromLogo: fromToken.logoUrl,
        toAmount: toAmount || quote.outputAmount,
        toSymbol: toToken.symbol,
        toLogo: toToken.logoUrl,
        txHash: lastTxHash,
        requestId: requestId,
        originChainId: fromToken.chainId,
      });
      
      // Clear form and reset approval state
      setFromAmount('');
      setToAmount('');
      setQuote(null);
      setNeedsApproval(false);
      setApprovalComplete(false);

    } catch (error: any) {
      console.error('[CustomSwap] Swap error:', error);
      
      // Parse common errors for better UX
      let errorMessage = 'Swap failed';
      const errorStr = error.message?.toLowerCase() || '';
      
      if (errorStr.includes('user rejected') || errorStr.includes('cancelled') || errorStr.includes('denied')) {
        errorMessage = 'Transaction cancelled by user';
      } else if (errorStr.includes('insufficient funds') || errorStr.includes('insufficient balance')) {
        errorMessage = 'Insufficient balance for this swap';
      } else if (errorStr.includes('allowance') || errorStr.includes('exceeds allowance') || errorStr.includes('transfer amount exceeds')) {
        // Allowance issue - need to re-approve
        errorMessage = 'Token allowance insufficient. Please approve the token again.';
        setNeedsApproval(true);
        setApprovalComplete(false);
        // Refetch quote to get fresh approval data
        fetchQuote();
      } else if (errorStr.includes('reverted') || errorStr.includes('execution reverted')) {
        errorMessage = 'Transaction reverted. The quote may have expired - please try again.';
        // Refetch quote to get fresh data
        fetchQuote();
      } else if (errorStr.includes('no solana wallet')) {
        errorMessage = error.message;
      } else if (errorStr.includes('failed to get fresh quote')) {
        errorMessage = 'Could not get a fresh quote. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsSwapping(false);
    }
  };

  // ============ Execute Approval ============
  const handleApprove = async () => {
    if (!fromToken || !toToken || !fromAmount || !quote?.rawQuote) {
      console.error('[CustomSwap] Missing required data for approval');
      return;
    }

    // Check if the selected source wallet is a Privy wallet
    const isUsingPrivyWallet = selectedSourceWallet?.type === 'privy';
    
    if (!isUsingPrivyWallet && !walletClient) {
      console.error('[CustomSwap] No wallet client available');
      return;
    }

    // Check if we need to switch chains (for EVM tokens)
    if (fromToken.chainId !== SOLANA_CHAIN_ID && connectedChainId !== fromToken.chainId) {
      console.log(`[CustomSwap] Need to switch from chain ${connectedChainId} to ${fromToken.chainId}`);
      try {
        await switchChainAsync({ chainId: fromToken.chainId });
        console.log('[CustomSwap] Chain switched successfully');
        // Small delay to let the wallet update
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (switchError: any) {
        console.error('[CustomSwap] Failed to switch chain:', switchError);
        if (switchError.message?.includes('User rejected')) {
          alert('Please switch to the correct network to approve this token.');
        } else {
          const chainName = SUPPORTED_CHAINS.find(c => c.id === fromToken.chainId)?.name || 'correct';
          alert(`Please switch your wallet to the ${chainName} network.`);
        }
        return;
      }
    }

    setIsApproving(true);

    try {
      const quoteData = quote.rawQuote;
      console.log('[CustomSwap] Executing approval with quote:', quoteData);
      
      // Find and execute the approval step
      if (quoteData.steps && quoteData.steps.length > 0) {
        for (const step of quoteData.steps) {
          const isApprovalStep = step.id === 'approve' || step.kind === 'approve';
          
          if (isApprovalStep && step.items && step.items.length > 0) {
            for (const item of step.items) {
              if (item.status === 'incomplete' && item.data) {
                const txData = item.data;
                const gasLimit = txData.gas ? BigInt(txData.gas) : undefined;
                
                console.log('[CustomSwap] Sending approval transaction:', {
                  to: txData.to,
                  gasLimit: gasLimit?.toString(),
                });

                // Send the approval transaction - use Privy wallet if selected
                let txHash: `0x${string}`;
                
                if (isUsingPrivyWallet) {
                  // Get Privy wallet and its provider - try multiple matching strategies
                  let privyWallet = wallets.find(w => 
                    w.walletClientType === 'privy' && 
                    w.type === 'ethereum' && 
                    w.address.toLowerCase() === selectedSourceWallet?.address.toLowerCase()
                  );
                  
                  // Fallback: match by chainType ethereum and address
                  if (!privyWallet) {
                    privyWallet = wallets.find(w => 
                      w.type === 'ethereum' && 
                      w.address.toLowerCase() === selectedSourceWallet?.address.toLowerCase()
                    );
                  }
                  
                  // Fallback: match by walletClientType privy and address (chainType might be undefined)
                  if (!privyWallet) {
                    privyWallet = wallets.find(w => 
                      w.walletClientType === 'privy' && 
                      w.address.toLowerCase() === selectedSourceWallet?.address.toLowerCase()
                    );
                    if (privyWallet) {
                      console.log('[CustomSwap] Found Privy wallet by walletClientType + address for approval');
                    }
                  }
                  
                  // Last fallback: match by address only
                  if (!privyWallet) {
                    privyWallet = wallets.find(w => 
                      w.address?.toLowerCase() === selectedSourceWallet?.address.toLowerCase()
                    );
                    if (privyWallet) {
                      console.log('[CustomSwap] Found wallet by address only for approval:', privyWallet.walletClientType, privyWallet.type);
                    }
                  }
                  
                  if (!privyWallet) {
                    console.error('[CustomSwap] Privy wallet not found for approval');
                    // Check if wagmi walletClient is connected to the same address as the selected Privy wallet
                    if (walletClient && address?.toLowerCase() === selectedSourceWallet?.address.toLowerCase()) {
                      console.log('[CustomSwap] Using wagmi walletClient for approval (same address as Privy wallet)');
                      txHash = await walletClient.sendTransaction({
                        to: txData.to as `0x${string}`,
                        data: txData.data as `0x${string}`,
                        value: BigInt(txData.value || 0),
                        gas: gasLimit,
                        maxFeePerGas: txData.maxFeePerGas ? BigInt(txData.maxFeePerGas) : undefined,
                        maxPriorityFeePerGas: txData.maxPriorityFeePerGas ? BigInt(txData.maxPriorityFeePerGas) : undefined,
                      });
                    } else {
                      // Cannot proceed - Privy wallet not available
                      alert(`Privy Smart Wallet (${selectedSourceWallet?.address.slice(0, 6)}...${selectedSourceWallet?.address.slice(-4)}) is not ready. Please try again in a moment, or switch to your connected wallet.`);
                      setIsLoadingQuote(false);
                      return;
                    }
                  } else {
                    // Switch Privy wallet to correct chain if needed
                    await privyWallet.switchChain(fromToken.chainId);
                    
                    const provider = await privyWallet.getEthereumProvider();
                    const txRequest = {
                      to: txData.to,
                      data: txData.data,
                      value: txData.value ? `0x${BigInt(txData.value).toString(16)}` : '0x0',
                      gas: gasLimit ? `0x${gasLimit.toString(16)}` : undefined,
                      maxFeePerGas: txData.maxFeePerGas ? `0x${BigInt(txData.maxFeePerGas).toString(16)}` : undefined,
                      maxPriorityFeePerGas: txData.maxPriorityFeePerGas ? `0x${BigInt(txData.maxPriorityFeePerGas).toString(16)}` : undefined,
                    };
                    
                    console.log('[CustomSwap] Sending approval via Privy wallet:', txRequest);
                    txHash = await provider.request({
                      method: 'eth_sendTransaction',
                      params: [txRequest],
                    }) as `0x${string}`;
                  }
                } else {
                  txHash = await walletClient!.sendTransaction({
                    to: txData.to as `0x${string}`,
                    data: txData.data as `0x${string}`,
                    value: BigInt(txData.value || 0),
                    gas: gasLimit,
                    maxFeePerGas: txData.maxFeePerGas ? BigInt(txData.maxFeePerGas) : undefined,
                    maxPriorityFeePerGas: txData.maxPriorityFeePerGas ? BigInt(txData.maxPriorityFeePerGas) : undefined,
                  });
                }

                console.log('[CustomSwap] Approval transaction sent:', txHash);

                // Wait for confirmation
                if (publicClient) {
                  const receipt = await publicClient.waitForTransactionReceipt({ 
                    hash: txHash,
                    timeout: 60_000,
                  });
                  
                  if (receipt.status === 'success') {
                    console.log('[CustomSwap] Approval confirmed:', receipt);
                    setApprovalComplete(true);
                    setNeedsApproval(false);
                    
                    // Auto-proceed to swap after a brief delay
                    console.log('[CustomSwap] Approval complete - will auto-proceed to swap');
                    // Note: The user will need to click "Swap" button now
                    // The state is set correctly, so button should show "Swap"
                  } else {
                    throw new Error('Approval transaction reverted');
                  }
                }
              }
            }
          }
        }
      }
    } catch (error: any) {
      console.error('[CustomSwap] Approval error:', error);
      
      let errorMessage = 'Approval failed';
      if (error.message?.includes('User rejected') || error.message?.includes('cancelled')) {
        errorMessage = 'Approval cancelled by user';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsApproving(false);
    }
  };

  // ============ Button State ============
  const getButtonText = () => {
    if (!isConnected && !authenticated) return 'Connect Wallet';
    if (!fromToken) return 'Select token';
    if (!toToken) return 'Select token';
    if (!fromAmount || parseFloat(fromAmount) <= 0) return 'Enter amount';
    if (isLoadingQuote) return 'Getting quote...';
    if (isApproving) return `Approving ${fromToken?.symbol || ''}...`;
    if (needsApproval && !approvalComplete) return `Approve ${fromToken?.symbol || ''}`;
    if (isSwapping) return 'Swapping...';
    return 'Swap';
  };

  const isButtonDisabled = () => {
    if (!isConnected && !authenticated) return false; // Can click to connect
    return !fromToken || !toToken || !fromAmount || parseFloat(fromAmount) <= 0 || isLoadingQuote || isSwapping || isApproving || !quote;
  };

  const handleButtonClick = () => {
    if (!isConnected && !authenticated) {
      login();
      return;
    }
    
    // If approval is needed and not yet complete, do approval first
    if (needsApproval && !approvalComplete) {
      handleApprove();
      return;
    }
    
    handleSwap();
  };

  // ============ USD Value Display ============
  const getUSDValue = (amount: string, isLoading: boolean) => {
    if (isLoading) return '~calculating...';
    if (!amount || parseFloat(amount) <= 0) return '~$0.00';
    // For now, we'll show from quote data
    const usdValue = typeof quote?.outputAmountUSD === 'number' ? quote.outputAmountUSD : parseFloat(quote?.outputAmountUSD || '0');
    return `~$${usdValue.toFixed(2)}`;
  };

  // ============ Render Token Logo ============
  const renderTokenLogo = (token: TokenInfo | null, size: number = 24) => {
    if (!token) return null;

    if (token.logoUrl) {
      return (
        <TokenLogo 
          src={token.logoUrl} 
          alt={token.symbol}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      );
    }

    return (
      <TokenLogoPlaceholder style={{ width: size, height: size }}>
        {token.symbol.slice(0, 2)}
      </TokenLogoPlaceholder>
    );
  };

  return (
    <>
      {/* Toast Notification */}
      {toastData && (
        <SwapSuccessToast
          data={toastData}
          onClose={() => setToastData(null)}
          duration={8000}
        />
      )}
      
      <WidgetContainer style={embedded ? { border: 'none', boxShadow: 'none' } : undefined}>
        {/* Hide header when embedded in modal (TradeModal has its own header) */}
        {!embedded && (
          <WidgetHeader>
            <WidgetTitle>Swap</WidgetTitle>
            <SettingsButton>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </SettingsButton>
          </WidgetHeader>
        )}

        {/* From Section */}
        <SwapSection>
          <SectionLabel>
            <span>From</span>
            {(isConnected || availableEvmWallets.length > 0 || availableSolanaWallets.length > 0) && (
              <WalletSelectorContainer>
                <WalletSelectorButton 
                  onClick={() => setSourceWalletSelectorOpen(!sourceWalletSelectorOpen)}
                  title="Select source wallet"
                >
                  {selectedSourceWallet ? (
                    <>
                      <span style={{ fontSize: '10px', color: selectedSourceWallet.type === 'privy' ? '#a855f7' : '#10b981' }}>
                        {selectedSourceWallet.type === 'privy' ? '🔐' : '🔗'}
                      </span>
                      {sourceWalletZNS || `${selectedSourceWallet.address.slice(0, 4)}...${selectedSourceWallet.address.slice(-4)}`}
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </>
                  ) : fromToken?.chainId === SOLANA_CHAIN_ID && availableSolanaWallets[0] ? (
                    <>
                      <span style={{ fontSize: '10px', color: availableSolanaWallets[0].type === 'privy' ? '#a855f7' : '#10b981' }}>
                        {availableSolanaWallets[0].type === 'privy' ? '🔐' : '🔗'}
                      </span>
                      {availableSolanaWallets[0].address.slice(0, 4)}...{availableSolanaWallets[0].address.slice(-4)}
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </>
                  ) : availableEvmWallets[0] ? (
                    <>
                      <span style={{ fontSize: '10px', color: availableEvmWallets[0].type === 'privy' ? '#a855f7' : '#10b981' }}>
                        {availableEvmWallets[0].type === 'privy' ? '🔐' : '🔗'}
                      </span>
                      {availableEvmWallets[0].address.slice(0, 6)}...{availableEvmWallets[0].address.slice(-4)}
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </>
                  ) : (
                    'Select wallet ▼'
                  )}
                </WalletSelectorButton>
                <WalletSelectorDropdown $isOpen={sourceWalletSelectorOpen}>
                  {fromToken?.chainId === SOLANA_CHAIN_ID ? (
                    availableSolanaWallets.map((wallet, idx) => (
                      <WalletOption
                        key={idx}
                        $selected={selectedSourceWallet?.address === wallet.address}
                        onClick={() => {
                          setSelectedSourceWallet({ ...wallet, chainType: 'solana' });
                          setSourceWalletSelectorOpen(false);
                        }}
                      >
                        <span style={{ color: wallet.type === 'privy' ? '#a855f7' : '#10b981' }}>
                          {wallet.type === 'privy' ? '🔐 ' : '🔗 '}
                        </span>
                        {wallet.label}
                        <span style={{ opacity: 0.6, marginLeft: 'auto', fontSize: '10px' }}>
                          {wallet.address.slice(0, 4)}...{wallet.address.slice(-4)}
                        </span>
                      </WalletOption>
                    ))
                  ) : (
                    availableEvmWallets.map((wallet, idx) => (
                      <WalletOption
                        key={idx}
                        $selected={selectedSourceWallet?.address === wallet.address}
                        onClick={() => {
                          setSelectedSourceWallet(wallet);
                          setSourceWalletSelectorOpen(false);
                        }}
                      >
                        <span style={{ color: wallet.type === 'privy' ? '#a855f7' : '#10b981' }}>
                          {wallet.type === 'privy' ? '🔐 ' : '🔗 '}
                        </span>
                        {wallet.label}
                        <span style={{ opacity: 0.6, marginLeft: 'auto', fontSize: '10px' }}>
                          {wallet.address.slice(0, 6)}...{wallet.address.slice(-4)}
                        </span>
                      </WalletOption>
                    ))
                  )}
                </WalletSelectorDropdown>
              </WalletSelectorContainer>
            )}
          </SectionLabel>
          <InputRow>
            <AmountInput
              type="text"
              inputMode="decimal"
              placeholder="0"
              value={fromAmount}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9.]/g, '');
                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                  setFromAmount(value);
                }
              }}
            />
            <TokenSelector 
              $hasToken={!!fromToken} 
              onClick={() => setSelectorOpen('from')}
            >
              {fromToken ? (
                <>
                  {renderTokenLogo(fromToken)}
                  <TokenSymbol>{fromToken.symbol}</TokenSymbol>
                </>
              ) : (
                <SelectTokenText>Select token</SelectTokenText>
              )}
              <ChevronIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </ChevronIcon>
            </TokenSelector>
          </InputRow>
          <USDValue>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            {getUSDValue(fromAmount, isLoadingQuote)}
          </USDValue>
          {/* Balance and percentage buttons */}
          {fromToken && (isConnected || (authenticated && selectedSourceWallet)) && (
            <BalanceRow>
              <BalanceText>
                Balance: <span>{fromToken.balance ? parseFloat(fromToken.balance).toFixed(6) : '0.00'} {fromToken.symbol}</span>
              </BalanceText>
              <PercentageButtons>
                <PercentButton onClick={() => handlePercentageClick(25)}>25%</PercentButton>
                <PercentButton onClick={() => handlePercentageClick(50)}>50%</PercentButton>
                <PercentButton onClick={() => handlePercentageClick(75)}>75%</PercentButton>
                <PercentButton onClick={() => handlePercentageClick(100)}>Max</PercentButton>
              </PercentageButtons>
            </BalanceRow>
          )}
        </SwapSection>

        {/* Swap Direction Button */}
        <SwapDirectionButton onClick={handleSwapDirection}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M7 16V4M7 4L3 8M7 4L11 8M17 8V20M17 20L21 16M17 20L13 16" />
          </svg>
        </SwapDirectionButton>

        {/* To Section */}
        <SwapSection>
          <SectionLabel>
            <span>To</span>
            {(isConnected || authenticated) && (
              <WalletIndicator>
                {toToken?.chainId === SOLANA_CHAIN_ID && selectedDestinationWallet
                  ? `${selectedDestinationWallet.address.slice(0, 4)}...${selectedDestinationWallet.address.slice(-4)}`
                  : toToken?.chainId === SOLANA_CHAIN_ID && solanaAddress
                    ? `${solanaAddress.slice(0, 4)}...${solanaAddress.slice(-4)}`
                    : address
                      ? `${address.slice(0, 6)}...${address.slice(-4)}`
                      : selectedSourceWallet?.address
                        ? `${selectedSourceWallet.address.slice(0, 6)}...${selectedSourceWallet.address.slice(-4)}`
                        : availableEvmWallets[0]?.address
                          ? `${availableEvmWallets[0].address.slice(0, 6)}...${availableEvmWallets[0].address.slice(-4)}`
                          : 'Connect wallet'
                }
              </WalletIndicator>
            )}
          </SectionLabel>
          <InputRow>
            <AmountInput
              type="text"
              placeholder="0"
              value={toAmount}
              readOnly
              style={{ color: toAmount ? '#ffffff' : 'rgba(255, 255, 255, 0.3)' }}
            />
            <TokenSelector 
              $hasToken={!!toToken} 
              onClick={() => setSelectorOpen('to')}
            >
              {toToken ? (
                <>
                  {renderTokenLogo(toToken)}
                  <TokenSymbol>{toToken.symbol}</TokenSymbol>
                </>
              ) : (
                <SelectTokenText>Select token</SelectTokenText>
              )}
              <ChevronIcon viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </ChevronIcon>
            </TokenSelector>
          </InputRow>
          <USDValue>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            ~${typeof quote?.outputAmountUSD === 'number' ? quote.outputAmountUSD.toFixed(2) : parseFloat(quote?.outputAmountUSD || '0').toFixed(2)}
          </USDValue>
          {/* Balance display for To token */}
          {toToken && (isConnected || authenticated) && (
            <BalanceRow>
              <BalanceText>
                Balance: <span>{toToken.balance ? parseFloat(toToken.balance).toFixed(6) : '0.00'} {toToken.symbol}</span>
              </BalanceText>
            </BalanceRow>
          )}
          {/* Wallet selector for Solana destination */}
          {toToken?.chainId === SOLANA_CHAIN_ID && availableSolanaWallets.length > 0 && (isConnected || authenticated) && (
            <WalletSelectorContainer>
              <WalletSelectorButton 
                onClick={() => setWalletSelectorOpen(!walletSelectorOpen)}
              >
                <span>
                  Receive to: {selectedDestinationWallet 
                    ? `${selectedDestinationWallet.label} (${selectedDestinationWallet.address.slice(0, 4)}...${selectedDestinationWallet.address.slice(-4)})`
                    : 'Select wallet'
                  }
                </span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points={walletSelectorOpen ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
                </svg>
              </WalletSelectorButton>
              <WalletSelectorDropdown $isOpen={walletSelectorOpen}>
                {availableSolanaWallets.map((wallet) => (
                  <WalletOption
                    key={wallet.address}
                    $selected={selectedDestinationWallet?.address === wallet.address}
                    onClick={() => {
                      setSelectedDestinationWallet(wallet);
                      setWalletSelectorOpen(false);
                    }}
                  >
                    <WalletOptionLabel>
                      <WalletOptionType>{wallet.label}</WalletOptionType>
                      <WalletOptionAddress>
                        {wallet.address.slice(0, 6)}...{wallet.address.slice(-4)}
                      </WalletOptionAddress>
                    </WalletOptionLabel>
                    <WalletBadge $type={wallet.type}>
                      {wallet.type === 'privy' ? 'PRIVY' : 'SELF'}
                    </WalletBadge>
                  </WalletOption>
                ))}
              </WalletSelectorDropdown>
            </WalletSelectorContainer>
          )}
          {/* Wallet selector for EVM destination */}
          {toToken && toToken.chainId !== SOLANA_CHAIN_ID && availableEvmWallets.length > 1 && (isConnected || authenticated) && (
            <WalletSelectorContainer>
              <WalletSelectorButton 
                onClick={() => setEvmDestinationSelectorOpen(!evmDestinationSelectorOpen)}
              >
                <span>
                  Receive to: {selectedDestinationEvmWallet 
                    ? `${selectedDestinationEvmWallet.label} (${selectedDestinationEvmWallet.address.slice(0, 4)}...${selectedDestinationEvmWallet.address.slice(-4)})`
                    : 'Select wallet'
                  }
                </span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points={evmDestinationSelectorOpen ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
                </svg>
              </WalletSelectorButton>
              <WalletSelectorDropdown $isOpen={evmDestinationSelectorOpen}>
                {availableEvmWallets.map((wallet) => (
                  <WalletOption
                    key={wallet.address}
                    $selected={selectedDestinationEvmWallet?.address === wallet.address}
                    onClick={() => {
                      setSelectedDestinationEvmWallet(wallet);
                      setEvmDestinationSelectorOpen(false);
                    }}
                  >
                    <WalletOptionLabel>
                      <WalletOptionType>{wallet.label}</WalletOptionType>
                      <WalletOptionAddress>
                        {wallet.address.slice(0, 6)}...{wallet.address.slice(-4)}
                      </WalletOptionAddress>
                    </WalletOptionLabel>
                    <WalletBadge $type={wallet.type}>
                      {wallet.type === 'privy' ? 'PRIVY' : 'SELF'}
                    </WalletBadge>
                  </WalletOption>
                ))}
              </WalletSelectorDropdown>
            </WalletSelectorContainer>
          )}
        </SwapSection>

        {/* Quote Info */}
        {quote && (
          <QuoteInfo>
            <QuoteRow>
              <span>Rate</span>
              <span>1 {fromToken?.symbol} ≈ {(parseFloat(quote.outputAmount) / parseFloat(fromAmount)).toFixed(6)} {toToken?.symbol}</span>
            </QuoteRow>
            <QuoteRow>
              <span>Price Impact</span>
              <span style={{ color: quote.priceImpact > 3 ? '#ef4444' : 'inherit' }}>
                {quote.priceImpact.toFixed(2)}%
              </span>
            </QuoteRow>
            {quote.fees && (
              <QuoteRow>
                <span>Network Fee</span>
                <span>{quote.fees.gas} ETH</span>
              </QuoteRow>
            )}
          </QuoteInfo>
        )}

        {/* Swap Button */}
        <SwapButton 
          $disabled={isButtonDisabled()}
          onClick={handleButtonClick}
        >
          {getButtonText()}
        </SwapButton>

        <PoweredBy>
          <span>powered by</span>
          <img src="/lockup_white.svg" alt="Relay" height="14" style={{ opacity: 0.5 }} />
        </PoweredBy>
      </WidgetContainer>

      {/* Token Selector Modal */}
      {selectorOpen && (
        <ModalOverlay onClick={() => {
          setSelectorOpen(null);
          setChainDropdownOpen(false);
          setSearchQuery('');
          setSelectedChainFilter('all');
          setCustomToken(null);
          setLookupError(null);
        }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Select a token</ModalTitle>
              <CloseButton onClick={() => {
                setSelectorOpen(null);
                setChainDropdownOpen(false);
                setSearchQuery('');
                setSelectedChainFilter('all');
                setCustomToken(null);
                setLookupError(null);
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </CloseButton>
            </ModalHeader>

            <SearchInput
              type="text"
              placeholder="Search by name or paste address"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />

            {/* Network Filter Dropdown */}
            <FilterRow>
              <FilterDropdown>
                <FilterButton onClick={() => setChainDropdownOpen(!chainDropdownOpen)}>
                  <FilterButtonContent>
                    {selectedChainFilter === 'all' ? (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        </svg>
                        <span>All networks</span>
                      </>
                    ) : (
                      <>
                        <FilterChainLogo 
                          src={getChainInfo(selectedChainFilter as number)?.logo} 
                          alt={getChainInfo(selectedChainFilter as number)?.name}
                        />
                        <span>{getChainInfo(selectedChainFilter as number)?.name}</span>
                      </>
                    )}
                  </FilterButtonContent>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </FilterButton>
                
                <FilterDropdownMenu $isOpen={chainDropdownOpen}>
                  <FilterOption 
                    $isSelected={selectedChainFilter === 'all'}
                    onClick={() => {
                      setSelectedChainFilter('all');
                      setChainDropdownOpen(false);
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                    <span>All networks</span>
                  </FilterOption>
                  {SUPPORTED_CHAINS.map((chain) => (
                    <FilterOption
                      key={chain.id}
                      $isSelected={selectedChainFilter === chain.id}
                      onClick={() => {
                        setSelectedChainFilter(chain.id);
                        setChainDropdownOpen(false);
                      }}
                    >
                      <ChainLogo src={chain.logo} alt={chain.name} />
                      <span>{chain.name}</span>
                    </FilterOption>
                  ))}
                </FilterDropdownMenu>
              </FilterDropdown>
            </FilterRow>

            <TokenList>
              {/* Custom Token Lookup Result */}
              {isLookingUpToken && (
                <LoadingSpinner>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }}>
                    <circle cx="12" cy="12" r="10" strokeOpacity="0.3" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                  Looking up token...
                </LoadingSpinner>
              )}
              
              {/* Custom Token Found */}
              {customToken && !isLookingUpToken && (
                <>
                  <div style={{ padding: '8px 16px', fontSize: '11px', color: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    Token found for address
                  </div>
                  <TokenItem
                    key={`custom-${customToken.chainId}-${customToken.address}`}
                    onClick={handleCustomTokenSelect}
                    style={{ background: 'rgba(168, 85, 247, 0.1)', borderBottom: '2px solid rgba(168, 85, 247, 0.3)' }}
                  >
                    <TokenLogoWrapper>
                      {customToken.logoUrl ? (
                        <TokenLogo 
                          src={customToken.logoUrl} 
                          alt={customToken.symbol}
                          style={{ width: 36, height: 36 }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <TokenLogoPlaceholder style={{ width: 36, height: 36, fontSize: 12 }}>
                          {customToken.symbol.slice(0, 2)}
                        </TokenLogoPlaceholder>
                      )}
                      {getChainInfo(customToken.chainId) && (
                        <TokenChainBadge src={getChainInfo(customToken.chainId)?.logo} alt={getChainInfo(customToken.chainId)?.name} />
                      )}
                    </TokenLogoWrapper>
                    <TokenItemInfo>
                      <TokenItemName>
                        {customToken.name}
                        <span style={{ marginLeft: '6px', padding: '2px 6px', fontSize: '9px', background: 'rgba(168, 85, 247, 0.3)', borderRadius: '4px', color: '#a855f7' }}>
                          IMPORT
                        </span>
                      </TokenItemName>
                      <TokenItemSymbol>
                        {customToken.symbol} • {customToken.address.slice(0, 6)}...{customToken.address.slice(-4)}
                      </TokenItemSymbol>
                    </TokenItemInfo>
                  </TokenItem>
                </>
              )}
              
              {/* Lookup Error */}
              {lookupError && !isLookingUpToken && !customToken && isContractAddress(searchQuery.trim()) && (
                <div style={{ padding: '16px', textAlign: 'center', color: 'rgba(255, 100, 100, 0.8)', fontSize: '12px' }}>
                  {lookupError}
                </div>
              )}
              
              {/* Regular Token List */}
              {isLoadingTokens ? (
                <LoadingSpinner>Loading tokens...</LoadingSpinner>
              ) : filteredTokens.length === 0 && !customToken && !isLookingUpToken ? (
                <div style={{ padding: '24px 16px', textAlign: 'center' }}>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '8px' }}>No tokens found</div>
                  <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px' }}>
                    {selectedChainFilter !== 'all' 
                      ? 'Try searching by name, symbol, or paste a contract address'
                      : 'Select a network and paste a contract address to import any token'
                    }
                  </div>
                </div>
              ) : (
                filteredTokens.map((token) => {
                  const chainInfo = getChainInfo(token.chainId);
                  return (
                    <TokenItem
                      key={`${token.chainId}-${token.address}`}
                      onClick={() => handleTokenSelect(token)}
                    >
                      <TokenLogoWrapper>
                        {token.logoUrl ? (
                          <TokenLogo 
                            src={token.logoUrl} 
                            alt={token.symbol}
                            style={{ width: 36, height: 36 }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <TokenLogoPlaceholder style={{ width: 36, height: 36, fontSize: 12 }}>
                            {token.symbol.slice(0, 2)}
                          </TokenLogoPlaceholder>
                        )}
                        {chainInfo && selectedChainFilter === 'all' && (
                          <TokenChainBadge src={chainInfo.logo} alt={chainInfo.name} />
                        )}
                      </TokenLogoWrapper>
                      <TokenItemInfo>
                        <TokenItemName>{token.name}</TokenItemName>
                        <TokenItemSymbol>{token.symbol}</TokenItemSymbol>
                      </TokenItemInfo>
                      {token.balance && (
                        <TokenItemBalance>
                          <TokenItemBalanceAmount>
                            {parseFloat(token.balance).toFixed(4)}
                          </TokenItemBalanceAmount>
                          {token.balanceUSD && (
                            <TokenItemBalanceUSD>
                              ${token.balanceUSD.toFixed(2)}
                            </TokenItemBalanceUSD>
                          )}
                        </TokenItemBalance>
                      )}
                    </TokenItem>
                  );
                })
              )}
            </TokenList>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default CustomSwapWidget;

